"use client"
import React from "react"
import { useState, useEffect, useRef, useMemo } from "react"
import {
  TrendingUp, Activity, AlertTriangle, BarChart3,
  PieChart as PieChartIcon, Truck, MapPin, Database,
  Package, ShoppingCart, RotateCcw, Layers,
  DollarSign, Clock, Settings, X, HelpCircle,
  XCircle, Search, Info, Server, Zap, Wifi,
  WifiOff, Timer, CheckCircle2, XOctagon, GitCompare,
  ArrowRight, ChevronDown, ChevronUp,
  LineChart as LineChartIcon, Brain,
} from "lucide-react"
import {
  PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer,
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  ComposedChart, ReferenceLine,
} from "recharts"
import { FileText, FileSpreadsheet, FileDown } from "lucide-react"
import { authFetch } from "../utils/authFetch";

const API_URL = process.env.REACT_APP_API_URL
  || (window.location.hostname === 'localhost'
    ? 'http://localhost:3001/api'
    : 'https://alvarezbohlback-production.up.railway.app/api')

// ============================================
// DATOS DEMO ESTÁTICOS PARA INTEGRACIÓN ERP
// ============================================

const DEMO_LATENCY_DATA = [
  { timestamp: "2026-02-10T08:01:00", operacion: "Entrada", codigo: "ENT-001", producto: "Aceite Oliva 1L", unidades: 500, latencia_nuestra: 42, latencia_erp: 14400, tipo: "entrada" },
  { timestamp: "2026-02-10T08:45:00", operacion: "Salida", codigo: "SAL-001", producto: "Leche UHT 1L", unidades: 200, latencia_nuestra: 67, latencia_erp: 7200, tipo: "salida" },
  { timestamp: "2026-02-10T09:30:00", operacion: "Ajuste", codigo: "AJU-001", producto: "Harina de Trigo 1Kg", unidades: -15, latencia_nuestra: 31, latencia_erp: 28800, tipo: "ajuste" },
  { timestamp: "2026-02-10T10:15:00", operacion: "Movimiento", codigo: "MOV-001", producto: "Azúcar Blanco 1Kg", unidades: 80, latencia_nuestra: 55, latencia_erp: 10800, tipo: "movimiento" },
  { timestamp: "2026-02-10T11:00:00", operacion: "Entrada", codigo: "ENT-002", producto: "Arroz Largo 1Kg", unidades: 300, latencia_nuestra: 38, latencia_erp: 18000, tipo: "entrada" },
  { timestamp: "2026-02-10T11:45:00", operacion: "Salida", codigo: "SAL-002", producto: "Agua Mineral 6u", unidades: 150, latencia_nuestra: 72, latencia_erp: 3600, tipo: "salida" },
  { timestamp: "2026-02-10T13:00:00", operacion: "Ajuste", codigo: "AJU-002", producto: "Tomate Frito 400g", unidades: -8, latencia_nuestra: 29, latencia_erp: 21600, tipo: "ajuste" },
  { timestamp: "2026-02-10T14:30:00", operacion: "Entrada", codigo: "ENT-003", producto: "Yogur Natural 4u", unidades: 400, latencia_nuestra: 61, latencia_erp: 86400, tipo: "entrada" },
  { timestamp: "2026-02-10T15:15:00", operacion: "Movimiento", codigo: "MOV-002", producto: "Sal Gruesa 1Kg", unidades: 60, latencia_nuestra: 44, latencia_erp: 14400, tipo: "movimiento" },
  { timestamp: "2026-02-10T16:00:00", operacion: "Salida", codigo: "SAL-003", producto: "Jabón Líquido 1L", unidades: 90, latencia_nuestra: 53, latencia_erp: 7200, tipo: "salida" },
  { timestamp: "2026-02-11T08:10:00", operacion: "Entrada", codigo: "ENT-004", producto: "Detergente Ropa 1Kg", unidades: 250, latencia_nuestra: 48, latencia_erp: 25200, tipo: "entrada" },
  { timestamp: "2026-02-11T09:00:00", operacion: "Salida", codigo: "SAL-004", producto: "Papel Higiénico 12u", unidades: 120, latencia_nuestra: 35, latencia_erp: 10800, tipo: "salida" },
  { timestamp: "2026-02-11T10:30:00", operacion: "Ajuste", codigo: "AJU-003", producto: "Galletas María 400g", unidades: -20, latencia_nuestra: 27, latencia_erp: 43200, tipo: "ajuste" },
  { timestamp: "2026-02-11T11:45:00", operacion: "Movimiento", codigo: "MOV-003", producto: "Cereales Muesli 500g", unidades: 70, latencia_nuestra: 66, latencia_erp: 18000, tipo: "movimiento" },
  { timestamp: "2026-02-11T13:15:00", operacion: "Entrada", codigo: "ENT-005", producto: "Mantequilla 250g", unidades: 180, latencia_nuestra: 41, latencia_erp: 7200, tipo: "entrada" },
  { timestamp: "2026-02-11T14:00:00", operacion: "Salida", codigo: "SAL-005", producto: "Queso Manchego 200g", unidades: 95, latencia_nuestra: 59, latencia_erp: 28800, tipo: "salida" },
  { timestamp: "2026-02-12T08:30:00", operacion: "Entrada", codigo: "ENT-006", producto: "Café Molido 250g", unidades: 350, latencia_nuestra: 37, latencia_erp: 14400, tipo: "entrada" },
  { timestamp: "2026-02-12T09:45:00", operacion: "Ajuste", codigo: "AJU-004", producto: "Conservas Atún 3u", unidades: -12, latencia_nuestra: 33, latencia_erp: 86400, tipo: "ajuste" },
  { timestamp: "2026-02-12T11:00:00", operacion: "Salida", codigo: "SAL-006", producto: "Pasta Espagueti 500g", unidades: 210, latencia_nuestra: 78, latencia_erp: 21600, tipo: "salida" },
  { timestamp: "2026-02-12T14:30:00", operacion: "Movimiento", codigo: "MOV-004", producto: "Vinagre Manzana 500ml", unidades: 40, latencia_nuestra: 45, latencia_erp: 10800, tipo: "movimiento" },
]

// DEMO_STOCK_ERP removed - now fetched from /analytics/erp/stock-comparison
const _UNUSED_DEMO_STOCK_ERP = [
  { codigo: "PRD-001", descripcion: "Aceite Oliva Virgen Extra 1L", categoria: "Alimentación", zona: "A", ubicacion: "A-01-01-01", stock_plataforma: 1240, stock_erp: 1198, unidad: "ud", precio: 4.95 },
  { codigo: "PRD-002", descripcion: "Leche UHT Entera 1L", categoria: "Lácteos", zona: "B", ubicacion: "B-02-01-01", stock_plataforma: 876, stock_erp: 876, unidad: "ud", precio: 0.89 },
  { codigo: "PRD-003", descripcion: "Harina de Trigo 1Kg", categoria: "Harinas", zona: "A", ubicacion: "A-03-02-01", stock_plataforma: 542, stock_erp: 527, unidad: "ud", precio: 0.75 },
  { codigo: "PRD-004", descripcion: "Azúcar Blanco 1Kg", categoria: "Alimentación", zona: "A", ubicacion: "A-03-02-02", stock_plataforma: 389, stock_erp: 389, unidad: "ud", precio: 1.20 },
  { codigo: "PRD-005", descripcion: "Arroz Largo 1Kg", categoria: "Cereales", zona: "C", ubicacion: "C-01-01-01", stock_plataforma: 712, stock_erp: 685, unidad: "ud", precio: 1.55 },
  { codigo: "PRD-006", descripcion: "Agua Mineral 6x1.5L", categoria: "Bebidas", zona: "D", ubicacion: "D-04-01-01", stock_plataforma: 320, stock_erp: 320, unidad: "paq", precio: 2.30 },
  { codigo: "PRD-007", descripcion: "Tomate Frito Casero 400g", categoria: "Conservas", zona: "B", ubicacion: "B-01-03-02", stock_plataforma: 234, stock_erp: 242, unidad: "ud", precio: 1.05 },
  { codigo: "PRD-008", descripcion: "Yogur Natural 4 unidades", categoria: "Lácteos", zona: "B", ubicacion: "B-02-02-01", stock_plataforma: 458, stock_erp: 440, unidad: "paq", precio: 1.80 },
  { codigo: "PRD-009", descripcion: "Sal Fina 1Kg", categoria: "Condimentos", zona: "A", ubicacion: "A-02-01-01", stock_plataforma: 156, stock_erp: 156, unidad: "ud", precio: 0.45 },
  { codigo: "PRD-010", descripcion: "Jabón Líquido Manos 500ml", categoria: "Higiene", zona: "E", ubicacion: "E-01-01-01", stock_plataforma: 289, stock_erp: 265, unidad: "ud", precio: 2.15 },
  { codigo: "PRD-011", descripcion: "Detergente Ropa Polvo 3Kg", categoria: "Limpieza", zona: "E", ubicacion: "E-02-01-01", stock_plataforma: 175, stock_erp: 175, unidad: "ud", precio: 8.90 },
  { codigo: "PRD-012", descripcion: "Papel Higiénico 12 rollos", categoria: "Higiene", zona: "E", ubicacion: "E-01-02-01", stock_plataforma: 420, stock_erp: 398, unidad: "paq", precio: 4.50 },
  { codigo: "PRD-013", descripcion: "Galletas María 400g", categoria: "Bollería", zona: "A", ubicacion: "A-04-01-01", stock_plataforma: 312, stock_erp: 332, unidad: "ud", precio: 1.25 },
  { codigo: "PRD-014", descripcion: "Cereales Muesli 500g", categoria: "Cereales", zona: "C", ubicacion: "C-02-01-01", stock_plataforma: 198, stock_erp: 198, unidad: "ud", precio: 3.20 },
  { codigo: "PRD-015", descripcion: "Mantequilla Sin Sal 250g", categoria: "Lácteos", zona: "B", ubicacion: "B-02-03-01", stock_plataforma: 267, stock_erp: 250, unidad: "ud", precio: 2.40 },
  { codigo: "PRD-016", descripcion: "Queso Manchego Curado 200g", categoria: "Lácteos", zona: "B", ubicacion: "B-03-01-01", stock_plataforma: 143, stock_erp: 143, unidad: "ud", precio: 5.80 },
  { codigo: "PRD-017", descripcion: "Café Molido Natural 250g", categoria: "Bebidas", zona: "A", ubicacion: "A-05-01-01", stock_plataforma: 389, stock_erp: 360, unidad: "ud", precio: 3.95 },
  { codigo: "PRD-018", descripcion: "Conservas Atún Claro 3u", categoria: "Conservas", zona: "B", ubicacion: "B-01-01-01", stock_plataforma: 521, stock_erp: 533, unidad: "paq", precio: 2.70 },
  { codigo: "PRD-019", descripcion: "Pasta Espagueti 500g", categoria: "Pastas", zona: "C", ubicacion: "C-03-01-01", stock_plataforma: 634, stock_erp: 634, unidad: "ud", precio: 0.95 },
  { codigo: "PRD-020", descripcion: "Vinagre de Manzana 500ml", categoria: "Condimentos", zona: "A", ubicacion: "A-02-02-01", stock_plataforma: 178, stock_erp: 168, unidad: "ud", precio: 1.65 },
  { codigo: "PRD-021", descripcion: "Zumo Naranja 1L", categoria: "Bebidas", zona: "D", ubicacion: "D-01-01-01", stock_plataforma: 445, stock_erp: 445, unidad: "ud", precio: 1.90 },
  { codigo: "PRD-022", descripcion: "Lejía Universal 1.5L", categoria: "Limpieza", zona: "E", ubicacion: "E-03-01-01", stock_plataforma: 231, stock_erp: 215, unidad: "ud", precio: 1.10 },
  { codigo: "PRD-023", descripcion: "Mermelada Fresa 350g", categoria: "Conservas", zona: "B", ubicacion: "B-01-02-01", stock_plataforma: 189, stock_erp: 189, unidad: "ud", precio: 2.25 },
  { codigo: "PRD-024", descripcion: "Cacahuetes Tostados 200g", categoria: "Snacks", zona: "F", ubicacion: "F-01-01-01", stock_plataforma: 304, stock_erp: 320, unidad: "ud", precio: 1.75 },
  { codigo: "PRD-025", descripcion: "Chocolate Negro 72% 100g", categoria: "Dulces", zona: "F", ubicacion: "F-02-01-01", stock_plataforma: 256, stock_erp: 256, unidad: "ud", precio: 2.10 },
]

const DEMO_LATENCY_HISTORY = [
  { fecha: "2026-02-10", avg_nuestra: 51, avg_erp: 15840, operaciones: 10, p95_nuestra: 78, p95_erp: 86400 },
  { fecha: "2026-02-11", avg_nuestra: 46, avg_erp: 22680, operaciones: 8, p95_nuestra: 66, p95_erp: 86400 },
  { fecha: "2026-02-12", avg_nuestra: 48, avg_erp: 33600, operaciones: 6, p95_nuestra: 78, p95_erp: 86400 },
  { fecha: "2026-02-13", avg_nuestra: 44, avg_erp: 18000, operaciones: 12, p95_nuestra: 72, p95_erp: 43200 },
  { fecha: "2026-02-14", avg_nuestra: 52, avg_erp: 28800, operaciones: 9, p95_nuestra: 81, p95_erp: 86400 },
  { fecha: "2026-02-15", avg_nuestra: 39, avg_erp: 14400, operaciones: 14, p95_nuestra: 61, p95_erp: 28800 },
  { fecha: "2026-02-16", avg_nuestra: 57, avg_erp: 21600, operaciones: 11, p95_nuestra: 84, p95_erp: 86400 },
  { fecha: "2026-02-17", avg_nuestra: 43, avg_erp: 16200, operaciones: 7, p95_nuestra: 68, p95_erp: 43200 },
]

// ============================================
// HELPERS GLOBALES
// ============================================

const linearRegression = (points) => {
  const n = points.length
  if (n < 2) return { slope: 0, intercept: points[0]?.y || 0, r2: 0 }
  const meanX = points.reduce((s, p) => s + p.x, 0) / n
  const meanY = points.reduce((s, p) => s + p.y, 0) / n
  const num = points.reduce((s, p) => s + (p.x - meanX) * (p.y - meanY), 0)
  const den = points.reduce((s, p) => s + (p.x - meanX) ** 2, 0)
  const slope = den !== 0 ? num / den : 0
  const intercept = meanY - slope * meanX
  const ssTot = points.reduce((s, p) => s + (p.y - meanY) ** 2, 0)
  const ssRes = points.reduce((s, p) => s + (p.y - (slope * p.x + intercept)) ** 2, 0)
  const r2 = ssTot !== 0 ? Math.max(0, 1 - ssRes / ssTot) : 0
  return { slope, intercept, r2 }
}

const exponentialMovingAverage = (data, alpha = 0.3) => {
  if (!data.length) return []
  const result = [data[0]]
  for (let i = 1; i < data.length; i++) {
    result.push(alpha * data[i] + (1 - alpha) * result[i - 1])
  }
  return result
}

// ============================================
// COMPONENTE TOOLTIP DE AYUDA
// ============================================
const HelpTooltip = ({ text }) => {
  const [isVisible, setIsVisible] = useState(false)
  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        onClick={() => setIsVisible(!isVisible)}
        style={{ background: 'transparent', border: 'none', cursor: 'help', padding: 0, display: 'flex', alignItems: 'center' }}
      >
        <HelpCircle size={14} color="#94a3b8" />
      </button>
      {isVisible && (
        <div style={{
          position: 'absolute', bottom: 'calc(100% + 8px)', left: '50%', transform: 'translateX(-50%)',
          background: '#1e293b', color: 'white', padding: '8px 12px', borderRadius: '6px', fontSize: '11px',
          maxWidth: '250px', whiteSpace: 'normal', zIndex: 1000,
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)', lineHeight: '1.4'
        }}>
          {text}
          <div style={{
            position: 'absolute', bottom: '-4px', left: '50%', transform: 'translateX(-50%)',
            width: 0, height: 0, borderLeft: '4px solid transparent', borderRight: '4px solid transparent', borderTop: '4px solid #1e293b'
          }} />
        </div>
      )}
    </div>
  )
}

// ============================================
// MODAL DE LEYENDA KPI
// ============================================
const LegendModal = ({ isOpen, onClose, leyenda }) => {
  if (!isOpen) return null
  return (
    <>
      <div onClick={onClose} style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)',
        zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center'
      }}>
        <div onClick={(e) => e.stopPropagation()} style={{
          background: 'white', borderRadius: '8px', padding: '20px', maxWidth: '600px', width: '90%',
          maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', paddingBottom: '12px', borderBottom: '1px solid #e2e8f0' }}>
            <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>Escala de Puntuación KPI</h3>
            <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center' }}>
              <X size={18} color="#64748b" />
            </button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: '10px', fontSize: '11px' }}>
            {leyenda.map(item => (
              <div key={item.puntuacion} style={{ padding: '12px', borderRadius: '6px', border: `2px solid ${item.color}`, backgroundColor: `${item.color}15`, textAlign: 'center' }}>
                <div style={{ fontWeight: 'bold', fontSize: '20px', color: item.color, marginBottom: '4px' }}>{item.puntuacion}</div>
                <div style={{ color: '#666', marginBottom: '4px', fontSize: '11px', fontWeight: '600' }}>{item.descripcion}</div>
                <div style={{ color: '#999', fontSize: '10px' }}>{item.etiqueta}</div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: '16px', padding: '12px', background: '#f8fafc', borderRadius: '6px', fontSize: '11px', color: '#64748b', lineHeight: '1.5' }}>
            <strong style={{ color: '#334155' }}>Nota:</strong> El KPI se calcula en base al tiempo de ejecución de cada proceso.
          </div>
        </div>
      </div>
    </>
  )
}

const ChartTypeSelector = ({ value, onChange, options }) => {
  const [isOpen, setIsOpen] = useState(false)
  if (!options || options.length === 0) return null
  return (
    <div style={{ position: 'relative' }}>
      <button onClick={() => setIsOpen(!isOpen)} style={{ padding: '4px 8px', background: 'white', border: '1px solid #e2e8f0', borderRadius: '3px', cursor: 'pointer', fontSize: '11px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '4px' }}>
        <Settings size={12} />
      </button>
      {isOpen && (
        <>
          <div style={{ position: 'absolute', top: 'calc(100% + 4px)', right: 0, minWidth: '130px', background: 'white', border: '1px solid #e2e8f0', borderRadius: '4px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 100 }}>
            {options.map(option => (
              <button key={option.value} onClick={() => { onChange(option.value); setIsOpen(false) }}
                style={{ display: 'flex', alignItems: 'center', gap: '6px', width: '100%', padding: '6px 10px', border: 'none', background: value === option.value ? '#f1f5f9' : 'white', color: value === option.value ? '#2563eb' : '#334155', fontSize: '11px', cursor: 'pointer', textAlign: 'left' }}
                onMouseEnter={(e) => { if (value !== option.value) e.currentTarget.style.background = '#f8fafc' }}
                onMouseLeave={(e) => { if (value !== option.value) e.currentTarget.style.background = 'white' }}>
                {option.icon && <option.icon size={11} />}
                {option.label}
              </button>
            ))}
          </div>
          <div onClick={() => setIsOpen(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 99 }} />
        </>
      )}
    </div>
  )
}

// ============================================
// HELPERS ERP
// ============================================

const formatSeconds = (s) => {
  if (s < 60) return `${s}s`
  if (s < 3600) return `${Math.round(s / 60)}m`
  if (s < 86400) return `${Math.round(s / 3600)}h`
  return `${Math.round(s / 86400)}d`
}

const calcDiff = (plat, erp) => {
  if (erp === 0) return 0
  return (((plat - erp) / erp) * 100).toFixed(1)
}

const getDiffColor = (diff) => {
  const d = Math.abs(parseFloat(diff))
  if (d === 0) return '#10b981'
  if (d <= 2) return '#f59e0b'
  if (d <= 5) return '#ef4444'
  return '#dc2626'
}

const LatencyTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null
  return (
    <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '10px 14px', fontSize: '11px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
      <p style={{ margin: '0 0 6px', fontWeight: '600', color: '#1e293b' }}>{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ margin: '2px 0', color: p.color }}>
          {p.name}: <strong>{formatSeconds(p.value)}</strong>
        </p>
      ))}
    </div>
  )
}

// ============================================
// PREDICCIÓN DE DEMANDA — mismo estilo que el resto de tarjetas
// ============================================

const DemandTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null
  const isPred = payload[0]?.payload?.isPrediction
  return (
    <div style={{
      background: 'white', border: '1px solid #e2e8f0',
      borderRadius: '6px', padding: '10px 14px', fontSize: '11px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
    }}>
      <p style={{ margin: '0 0 6px', fontWeight: '600', color: '#1e293b' }}>
        {isPred ? '🔮 ' : '📦 '}{label}
      </p>
      {payload.map((p, i) => p.value != null && (
        <p key={i} style={{ margin: '2px 0', color: p.color }}>
          {p.name}: <strong>{Math.round(p.value)} uds</strong>
        </p>
      ))}
    </div>
  )
}

const DemandPredictionPanel = ({ token, startDate, endDate }) => {
  const [searchText, setSearchText]         = useState('')
  const [searchResults, setSearchResults]   = useState([])
  const [showDropdown, setShowDropdown]     = useState(false)
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [historicalData, setHistoricalData] = useState([])
  const [loadingSearch, setLoadingSearch]   = useState(false)
  const [loadingData, setLoadingData]       = useState(false)
  const [predictionDays, setPredictionDays] = useState(14)
  const [method, setMethod]                 = useState('lineal')
  const [chartType, setChartType]           = useState('bar_line')
  const [errorMsg, setErrorMsg]             = useState(null)
  const debounceRef = useRef(null)

  const handleSearchChange = (text) => {
    setSearchText(text)
    clearTimeout(debounceRef.current)
    if (text.length < 2) { setSearchResults([]); setShowDropdown(false); return }
    setLoadingSearch(true)
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await authFetch(
          `${API_URL}/analytics/demand-prediction?search=${encodeURIComponent(text)}`,
          { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } }
        )
        if (!res) return;
        const data = await res.json()
        setSearchResults(data.data?.products || [])
        setShowDropdown(true)
      } catch { setSearchResults([]) }
      finally { setLoadingSearch(false) }
    }, 350)
  }

  const handleSelectProduct = async (product) => {
    setSelectedProduct(product)
    setSearchText(`[${product.codigo}] ${product.descripcion}`)
    setShowDropdown(false)
    setLoadingData(true)
    setErrorMsg(null)
    try {
      const params = new URLSearchParams({ productCode: product.codigo, startDate, endDate })
      const res = await authFetch(
        `${API_URL}/analytics/demand-prediction?${params}`,
        { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } }
      )
      if (!res) return;
      const data = await res.json()
      if (data.success) {
        setHistoricalData(data.data?.historicalDemand || [])
      } else {
        setErrorMsg(data.error || 'Error al cargar datos')
        setHistoricalData([])
      }
    } catch { setErrorMsg('Error de conexión'); setHistoricalData([]) }
    finally { setLoadingData(false) }
  }

  const { chartData, kpis } = useMemo(() => {
    if (!historicalData.length) return { chartData: [], kpis: {}, regressionInfo: null }

    const validData = historicalData.map((d, i) => ({ ...d, x: i, y: d.units }))
    const emaValues = exponentialMovingAverage(validData.map(d => d.y), 0.3)
    const reg = linearRegression(validData)

    const historical = validData.map((d, i) => ({
      date: d.date,
      historico: d.units,
      ema: Math.max(0, Math.round(emaValues[i])),
      prediccion: null,
      isPrediction: false,
      label: new Date(d.date + 'T00:00:00').toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' })
    }))

    const lastDate = new Date(historicalData[historicalData.length - 1].date + 'T00:00:00')
    const lastEma  = emaValues[emaValues.length - 1]

    const predictions = Array.from({ length: predictionDays }, (_, i) => {
      const x = validData.length + i
      const linPred  = Math.max(0, Math.round(reg.slope * x + reg.intercept))
      const emaPred  = Math.max(0, Math.round(lastEma + reg.slope * (i + 1)))
      const predicted = method === 'lineal' ? linPred : emaPred
      const futureDate = new Date(lastDate)
      futureDate.setDate(futureDate.getDate() + i + 1)
      return {
        date: futureDate.toISOString().split('T')[0],
        historico: null,
        ema: null,
        prediccion: predicted,
        isPrediction: true,
        label: futureDate.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' })
      }
    })

    const allData = [...historical, ...predictions]

    const avgHistorico  = Math.round(validData.reduce((s, d) => s + d.y, 0) / validData.length)
    const maxHistorico  = Math.max(...validData.map(d => d.y))
    const minHistorico  = Math.min(...validData.map(d => d.y))
    const pred7         = predictions.slice(0, 7).reduce((s, d) => s + (d.prediccion || 0), 0)
    const pred14        = predictions.reduce((s, d) => s + (d.prediccion || 0), 0)
    const avgPrediction = Math.round(pred14 / predictionDays)
    const tendencia     = reg.slope > 0.5 ? 'creciente' : reg.slope < -0.5 ? 'decreciente' : 'estable'
    const confianza     = Math.round(reg.r2 * 100)

    return {
      chartData: allData,
      kpis: { avgHistorico, maxHistorico, minHistorico, pred7, pred14, avgPrediction, tendencia, confianza },
      regressionInfo: reg
    }
  }, [historicalData, predictionDays, method])

  const todayStr = new Date().toISOString().split('T')[0]

  const TENDENCIA_CONFIG = {
    creciente:  { color: '#10b981', label: 'Creciente ↑' },
    decreciente:{ color: '#ef4444', label: 'Decreciente ↓' },
    estable:    { color: '#f59e0b', label: 'Estable →' },
  }

  const chartOptions = [
    { value: 'bar_line',  label: 'Barras + Línea', icon: BarChart3 },
    { value: 'area_line', label: 'Área + Línea',   icon: Activity },
    { value: 'dual_line', label: 'Doble Línea',    icon: LineChartIcon },
  ]

  const renderChart = () => {
    const commonProps = {
      data: chartData,
      margin: { top: 5, right: 10, left: -10, bottom: 5 }
    }
    const xAxis = (
      <XAxis dataKey="label" fontSize={9} tick={{ fill: '#94a3b8' }} tickLine={false} axisLine={false}
        interval={Math.max(0, Math.floor(chartData.length / 12))} />
    )
    const yAxis = (
      <YAxis fontSize={9} tick={{ fill: '#94a3b8' }} tickLine={false} axisLine={false} />
    )
    const grid = <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
    const tip  = <Tooltip content={<DemandTooltip />} />
    const todayRef = chartData.findIndex(d => d.date === todayStr) >= 0 ? (
      <ReferenceLine
        x={new Date(todayStr + 'T00:00:00').toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' })}
        stroke="#ef4444" strokeDasharray="4 4" strokeOpacity={0.6} strokeWidth={1.5}
        label={{ value: 'Hoy', position: 'top', fontSize: 8, fill: '#ef4444' }}
      />
    ) : null
    const splitRef = chartData.find(d => d.isPrediction) ? (
      <ReferenceLine
        x={chartData.find(d => d.isPrediction)?.label}
        stroke="#94a3b8" strokeDasharray="3 3" strokeOpacity={0.5} strokeWidth={1}
        label={{ value: 'Forecast ▶', position: 'insideTopRight', fontSize: 8, fill: '#94a3b8' }}
      />
    ) : null

    if (chartType === 'bar_line') {
      return (
        <ComposedChart {...commonProps}>
          <defs>
            <linearGradient id="gradBar" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.9} />
              <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.5} />
            </linearGradient>
          </defs>
          {grid}{xAxis}{yAxis}{tip}{todayRef}{splitRef}
          <Bar dataKey="historico" fill="url(#gradBar)" radius={[3,3,0,0]} name="Histórico" maxBarSize={18} />
          <Line type="monotone" dataKey="prediccion" stroke="#f59e0b" strokeWidth={2.5}
            strokeDasharray="6 3" dot={(p) => {
              const { cx, cy, payload } = p
              if (!payload.isPrediction || payload.prediccion == null) return null
              return <circle key={`d${p.index}`} cx={cx} cy={cy} r={3} fill="#f59e0b" stroke="white" strokeWidth={1.5} />
            }}
            activeDot={{ r: 5, fill: '#f59e0b', stroke: 'white', strokeWidth: 2 }}
            connectNulls={false} name="Predicción" />
          {method === 'ema' && (
            <Line type="monotone" dataKey="ema" stroke="#10b981" strokeWidth={1.5}
              strokeDasharray="4 2" dot={false} connectNulls={false} name="EMA" />
          )}
        </ComposedChart>
      )
    }

    if (chartType === 'area_line') {
      return (
        <ComposedChart {...commonProps}>
          <defs>
            <linearGradient id="gradHist" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%"  stopColor="#3b82f6" stopOpacity={0.2} />
              <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="gradPred" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%"  stopColor="#f59e0b" stopOpacity={0.2} />
              <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
            </linearGradient>
          </defs>
          {grid}{xAxis}{yAxis}{tip}{todayRef}{splitRef}
          <Area type="monotone" dataKey="historico" stroke="#3b82f6" strokeWidth={2.5}
            fill="url(#gradHist)" dot={false}
            activeDot={{ r: 5, fill: '#3b82f6', stroke: 'white', strokeWidth: 2 }}
            connectNulls={false} name="Histórico" />
          <Area type="monotone" dataKey="prediccion" stroke="#f59e0b" strokeWidth={2.5}
            strokeDasharray="7 3" fill="url(#gradPred)"
            dot={(p) => {
              const { cx, cy, payload } = p
              if (!payload.isPrediction || payload.prediccion == null) return null
              return <circle key={`d${p.index}`} cx={cx} cy={cy} r={3} fill="#f59e0b" stroke="white" strokeWidth={1.5} />
            }}
            activeDot={{ r: 5, fill: '#f59e0b', stroke: 'white', strokeWidth: 2 }}
            connectNulls={false} name="Predicción" />
          {method === 'ema' && (
            <Line type="monotone" dataKey="ema" stroke="#10b981" strokeWidth={1.5}
              strokeDasharray="4 2" dot={false} connectNulls={false} name="EMA" />
          )}
        </ComposedChart>
      )
    }

    // dual_line
    return (
      <LineChart {...commonProps}>
        {grid}{xAxis}{yAxis}{tip}{todayRef}{splitRef}
        <Line type="monotone" dataKey="historico" stroke="#3b82f6" strokeWidth={2.5}
          dot={false} activeDot={{ r: 5, fill: '#3b82f6', stroke: 'white', strokeWidth: 2 }}
          connectNulls={false} name="Histórico" />
        <Line type="monotone" dataKey="prediccion" stroke="#f59e0b" strokeWidth={2.5}
          strokeDasharray="7 3"
          dot={(p) => {
            const { cx, cy, payload } = p
            if (!payload.isPrediction || payload.prediccion == null) return null
            return <circle key={`d${p.index}`} cx={cx} cy={cy} r={3} fill="#f59e0b" stroke="white" strokeWidth={1.5} />
          }}
          activeDot={{ r: 5, fill: '#f59e0b', stroke: 'white', strokeWidth: 2 }}
          connectNulls={false} name="Predicción" />
        {method === 'ema' && (
          <Line type="monotone" dataKey="ema" stroke="#10b981" strokeWidth={1.5}
            strokeDasharray="4 2" dot={false} connectNulls={false} name="EMA" />
        )}
      </LineChart>
    )
  }

  return (
    <div style={{ gridColumn: '1 / -1', background: 'white', border: '1px solid #e2e8f0', borderRadius: '4px' }}>
      {/* ─── Header: mismo estilo que otras tarjetas ─── */}
      <div style={{ padding: '10px 12px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Brain size={14} color="#2563eb" />
          <h3 style={{ margin: 0, fontSize: '12px', fontWeight: '600', color: '#334155' }}>Predicción de Demanda</h3>
          <HelpTooltip text="Proyección de salidas futuras por producto usando regresión lineal o media móvil exponencial (EMA). R² mide la confianza del modelo." />
          {selectedProduct && (
            <span style={{ padding: '2px 7px', background: '#dbeafe', color: '#1d4ed8', borderRadius: '10px', fontSize: '10px', fontWeight: '600' }}>
              [{selectedProduct.codigo}] {selectedProduct.descripcion.substring(0, 30)}{selectedProduct.descripcion.length > 30 ? '…' : ''}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
          {/* Días de predicción */}
          <div style={{ display: 'flex', gap: '2px', background: '#f1f5f9', borderRadius: '3px', padding: '2px' }}>
            {[7, 14, 30].map(d => (
              <button key={d} onClick={() => setPredictionDays(d)} style={{
                padding: '3px 8px', borderRadius: '2px', border: 'none', cursor: 'pointer',
                fontSize: '10px', fontWeight: '600',
                background: predictionDays === d ? 'white' : 'transparent',
                color: predictionDays === d ? '#2563eb' : '#64748b',
                boxShadow: predictionDays === d ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
              }}>
                {d}d
              </button>
            ))}
          </div>
          {/* Método */}
          <div style={{ display: 'flex', gap: '2px', background: '#f1f5f9', borderRadius: '3px', padding: '2px' }}>
            {[{ v: 'lineal', l: 'Lineal' }, { v: 'ema', l: 'EMA' }].map(m => (
              <button key={m.v} onClick={() => setMethod(m.v)} style={{
                padding: '3px 8px', borderRadius: '2px', border: 'none', cursor: 'pointer',
                fontSize: '10px', fontWeight: '600',
                background: method === m.v ? 'white' : 'transparent',
                color: method === m.v ? '#2563eb' : '#64748b',
                boxShadow: method === m.v ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
              }}>
                {m.l}
              </button>
            ))}
          </div>
          <ChartTypeSelector value={chartType} onChange={setChartType} options={chartOptions} />
        </div>
      </div>

      {/* ─── Body ─── */}
      <div style={{ padding: '12px' }}>
        {/* Buscador de producto */}
        <div style={{ position: 'relative', marginBottom: '12px' }}>
          <Search size={13} color="#94a3b8" style={{ position: 'absolute', left: '9px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
          <input
            type="text"
            placeholder="Buscar producto por código o nombre para analizar su demanda..."
            value={searchText}
            onChange={e => handleSearchChange(e.target.value)}
            onFocus={() => searchResults.length > 0 && setShowDropdown(true)}
            style={{
              width: '100%', padding: '7px 32px 7px 30px',
              border: '1px solid #e2e8f0', borderRadius: '3px', fontSize: '11px',
              outline: 'none', boxSizing: 'border-box', background: '#f8fafc',
            }}
          />
          {loadingSearch && (
            <div style={{ position: 'absolute', right: '9px', top: '50%', transform: 'translateY(-50%)' }}>
              <div style={{ width: '12px', height: '12px', border: '2px solid #e2e8f0', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            </div>
          )}
          {searchText && !loadingSearch && (
            <button onClick={() => { setSearchText(''); setSelectedProduct(null); setHistoricalData([]); setSearchResults([]); setShowDropdown(false) }}
              style={{ position: 'absolute', right: '9px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#94a3b8' }}>
              <X size={12} />
            </button>
          )}

          {/* Dropdown */}
          {showDropdown && searchResults.length > 0 && (
            <>
              <div onClick={() => setShowDropdown(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 98 }} />
              <div style={{
                position: 'absolute', top: 'calc(100% + 2px)', left: 0, right: 0,
                background: 'white', border: '1px solid #e2e8f0', borderRadius: '4px',
                boxShadow: '0 4px 16px rgba(0,0,0,0.1)', zIndex: 99, maxHeight: '220px', overflowY: 'auto'
              }}>
                {searchResults.map((p, i) => (
                  <button key={p.codigo} onClick={() => handleSelectProduct(p)}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '8px 12px', border: 'none', background: 'white', cursor: 'pointer', textAlign: 'left', borderBottom: i < searchResults.length - 1 ? '1px solid #f8fafc' : 'none' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                    onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                    <Package size={12} color="#3b82f6" style={{ flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '11px', fontWeight: '600', color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.descripcion}</div>
                      <div style={{ display: 'flex', gap: '6px', marginTop: '1px' }}>
                        <code style={{ fontSize: '9px', color: '#64748b', background: '#f1f5f9', padding: '1px 4px', borderRadius: '2px' }}>{p.codigo}</code>
                        {p.categoria && <span style={{ fontSize: '9px', color: '#94a3b8' }}>{p.categoria}</span>}
                      </div>
                    </div>
                    <ArrowRight size={10} color="#cbd5e1" />
                  </button>
                ))}
              </div>
            </>
          )}
          {showDropdown && searchResults.length === 0 && !loadingSearch && searchText.length >= 2 && (
            <div style={{ position: 'absolute', top: 'calc(100% + 2px)', left: 0, right: 0, background: 'white', border: '1px solid #e2e8f0', borderRadius: '4px', padding: '12px', textAlign: 'center', zIndex: 99, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
              <p style={{ margin: 0, fontSize: '11px', color: '#94a3b8' }}>Sin resultados para "{searchText}"</p>
            </div>
          )}
        </div>

        {/* Estado inicial */}
        {!selectedProduct && !loadingData && (
          <div style={{ padding: '32px 24px', textAlign: 'center', background: '#f8fafc', borderRadius: '3px', border: '1px solid #f1f5f9' }}>
            <Brain size={28} color="#cbd5e1" style={{ marginBottom: '8px' }} />
            <p style={{ margin: '0 0 4px', fontSize: '12px', fontWeight: '600', color: '#64748b' }}>Selecciona un producto para analizar su demanda</p>
            <p style={{ margin: 0, fontSize: '11px', color: '#94a3b8' }}>
              Regresión lineal · Media móvil exponencial · Confianza R² · Proyección {predictionDays} días
            </p>
          </div>
        )}

        {/* Loading */}
        {loadingData && (
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <div style={{ width: '32px', height: '32px', border: '3px solid #f1f5f9', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 10px' }} />
            <p style={{ margin: 0, fontSize: '11px', color: '#94a3b8' }}>Cargando historial de demanda...</p>
          </div>
        )}

        {/* Error */}
        {errorMsg && !loadingData && (
          <div style={{ padding: '10px 12px', background: '#fef2f2', borderRadius: '3px', border: '1px solid #fecaca', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
            <AlertTriangle size={14} color="#ef4444" />
            <span style={{ fontSize: '11px', color: '#dc2626' }}>{errorMsg}</span>
          </div>
        )}

        {/* Sin datos históricos */}
        {selectedProduct && !loadingData && historicalData.length === 0 && !errorMsg && (
          <div style={{ padding: '28px', textAlign: 'center', background: '#f8fafc', borderRadius: '3px' }}>
            <AlertTriangle size={24} color="#f59e0b" style={{ marginBottom: '8px' }} />
            <p style={{ margin: '0 0 4px', fontSize: '12px', fontWeight: '600', color: '#334155' }}>Sin datos históricos</p>
            <p style={{ margin: 0, fontSize: '11px', color: '#94a3b8' }}>No hay pedidos de salida en el período seleccionado para este producto.</p>
          </div>
        )}

        {/* Gráfico con datos */}
        {selectedProduct && !loadingData && historicalData.length > 0 && (
          <>
            {/* KPI strip */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
              {[
                { label: 'Media diaria', value: `${kpis.avgHistorico} uds`, color: '#3b82f6', bg: '#eff6ff' },
                { label: `Forecast ${predictionDays}d`, value: `${kpis.pred14} uds`, color: '#f59e0b', bg: '#fffbeb' },
                { label: 'Próx. 7 días', value: `${kpis.pred7} uds`, color: '#8b5cf6', bg: '#faf5ff' },
                { label: 'Tendencia', value: TENDENCIA_CONFIG[kpis.tendencia]?.label, color: TENDENCIA_CONFIG[kpis.tendencia]?.color, bg: '#f8fafc' },
                { label: 'Confianza R²', value: `${kpis.confianza}%`, color: kpis.confianza >= 70 ? '#10b981' : kpis.confianza >= 40 ? '#f59e0b' : '#ef4444', bg: '#f8fafc' },
                { label: 'Máx histórico', value: `${kpis.maxHistorico} uds`, color: '#10b981', bg: '#f0fdf4' },
              ].map((k, i) => (
                <div key={i} style={{ flex: '1', minWidth: '100px', padding: '8px 10px', background: k.bg, borderRadius: '3px', border: '1px solid #f1f5f9' }}>
                  <div style={{ fontSize: '9px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: '3px' }}>{k.label}</div>
                  <div style={{ fontSize: '14px', fontWeight: '700', color: k.color }}>{k.value}</div>
                </div>
              ))}
            </div>

            {/* Leyenda del gráfico */}
            <div style={{ display: 'flex', gap: '12px', marginBottom: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '10px', color: '#64748b' }}>
                <div style={{ width: '14px', height: chartType === 'bar_line' ? '10px' : '3px', background: '#3b82f6', borderRadius: '2px' }} />
                Histórico
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '10px', color: '#64748b' }}>
                <div style={{ width: '14px', height: '2px', borderTop: '2px dashed #f59e0b' }} />
                Predicción
              </div>
              {method === 'ema' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '10px', color: '#64748b' }}>
                  <div style={{ width: '14px', height: '2px', borderTop: '2px dashed #10b981' }} />
                  EMA (suavizado)
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '10px', color: '#64748b' }}>
                <div style={{ width: '2px', height: '12px', background: '#ef4444', opacity: 0.6 }} />
                Hoy
              </div>
              <div style={{ marginLeft: 'auto', fontSize: '10px', color: '#94a3b8' }}>
                {historicalData.length} días histórico · {predictionDays} días forecast
                · Método: {method === 'lineal' ? 'Regresión lineal' : 'EMA (α=0.3)'}
              </div>
            </div>

            {/* Chart */}
            <ResponsiveContainer width="100%" height={260}>
              {renderChart()}
            </ResponsiveContainer>

            {/* Tabla de predicción diaria compacta */}
            <div style={{ marginTop: '12px' }}>
              <div style={{ fontSize: '10px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: '6px' }}>
                Detalle predicción diaria
              </div>
              <div style={{ overflowX: 'auto' }}>
                <div style={{ display: 'flex', gap: '4px', minWidth: 'max-content' }}>
                  {chartData.filter(d => d.isPrediction).slice(0, predictionDays).map((d, i) => {
                    const max = Math.max(...chartData.filter(x => x.isPrediction).map(x => x.prediccion || 0))
                    const pct = max > 0 ? ((d.prediccion || 0) / max) * 100 : 0
                    const isWE = [0, 6].includes(new Date(d.date + 'T00:00:00').getDay())
                    return (
                      <div key={i} style={{ minWidth: '46px', padding: '6px 4px', background: isWE ? '#fef9c3' : '#f8fafc', borderRadius: '3px', border: `1px solid ${isWE ? '#fde68a' : '#e2e8f0'}`, textAlign: 'center' }}>
                        <div style={{ fontSize: '8px', color: '#94a3b8', marginBottom: '3px' }}>
                          {new Date(d.date + 'T00:00:00').toLocaleDateString('es-ES', { weekday: 'short' }).toUpperCase().slice(0,3)}
                        </div>
                        <div style={{ fontSize: '8px', color: '#64748b', marginBottom: '4px' }}>{d.label}</div>
                        <div style={{ height: '24px', background: '#e2e8f0', borderRadius: '2px', overflow: 'hidden', display: 'flex', alignItems: 'flex-end', marginBottom: '3px' }}>
                          <div style={{ width: '100%', height: `${pct}%`, background: '#f59e0b', borderRadius: '2px', minHeight: '2px' }} />
                        </div>
                        <div style={{ fontSize: '10px', fontWeight: '700', color: '#f59e0b' }}>{d.prediccion}</div>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>

            {/* Nota metodológica */}
            {kpis.confianza < 40 && (
              <div style={{ marginTop: '8px', padding: '7px 10px', background: '#fef9c3', borderRadius: '3px', border: '1px solid #fde68a', fontSize: '10px', color: '#92400e' }}>
                ⚠️ <strong>Confianza baja (R²={kpis.confianza}%).</strong> Considera ampliar el rango de fechas para mejorar la precisión del modelo.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

// ============================================
// Panel: Comparación Latencia Tiempo Real
// ============================================
const LatencyPanel = () => {
  const [selectedTipo, setSelectedTipo] = useState('todos')

  const tipoOps = ['todos', 'entrada', 'salida', 'ajuste', 'movimiento']
  const filteredData = selectedTipo === 'todos' ? DEMO_LATENCY_DATA : DEMO_LATENCY_DATA.filter(d => d.tipo === selectedTipo)

  const avgNuestra = Math.round(filteredData.reduce((s, d) => s + d.latencia_nuestra, 0) / filteredData.length)
  const avgERP     = Math.round(filteredData.reduce((s, d) => s + d.latencia_erp, 0) / filteredData.length)
  const mejora     = ((avgERP / avgNuestra)).toFixed(0)
  const totalOps   = filteredData.length

  const TIPO_COLORS = { entrada: '#10b981', salida: '#3b82f6', ajuste: '#f59e0b', movimiento: '#8b5cf6' }
  const TIPO_ICONS  = { entrada: '📦', salida: '📤', ajuste: '⚙️', movimiento: '🔄' }

  return (
    <div>
      <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
        {[
          { label: 'Latencia Nuestra Plataforma', value: `${avgNuestra}ms`, sub: 'Tiempo real', color: '#10b981', bg: '#f0fdf4', border: '#10b981', icon: <Zap size={20} color="#10b981" /> },
          { label: 'Latencia ERP Cliente', value: formatSeconds(avgERP), sub: 'Proceso manual/batch', color: '#ef4444', bg: '#fef2f2', border: '#ef4444', icon: <Clock size={20} color="#ef4444" /> },
          { label: 'Mejora de Velocidad', value: `${mejora}x`, sub: 'veces más rápido', color: '#2563eb', bg: '#eff6ff', border: '#2563eb', icon: <TrendingUp size={20} color="#2563eb" /> },
          { label: 'Operaciones Analizadas', value: totalOps, color: '#8b5cf6', bg: '#faf5ff', border: '#8b5cf6', icon: <Activity size={20} color="#8b5cf6" /> },
        ].map((k, i) => (
          <div key={i} style={{ flex: '1', minWidth: '140px', padding: '12px 14px', background: k.bg, borderRadius: '6px', borderLeft: `3px solid ${k.border}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
              {k.icon}
              <span style={{ fontSize: '10px', color: '#64748b', fontWeight: '500' }}>{k.label.toUpperCase()}</span>
            </div>
            <div style={{ fontSize: '22px', fontWeight: '800', color: k.color }}>{k.value}</div>
            <div style={{ fontSize: '9px', color: '#94a3b8', marginTop: '2px' }}>{k.sub}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
        <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '4px' }}>
          <div style={{ padding: '10px 12px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <BarChart3 size={14} color="#2563eb" />
            <h3 style={{ margin: 0, fontSize: '12px', fontWeight: '600', color: '#334155' }}>Latencia Media por Día</h3>
            <HelpTooltip text="Comparación del tiempo promedio de actualización: nuestra plataforma (ms) vs ERP cliente (horas)." />
          </div>
          <div style={{ padding: '12px' }}>
            <ResponsiveContainer width="100%" height={240}>
              <ComposedChart data={DEMO_LATENCY_HISTORY}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="fecha" fontSize={9} tick={{ fill: '#94a3b8' }} tickFormatter={v => v.slice(5)} />
                <YAxis yAxisId="left"  orientation="left"  fontSize={9} tick={{ fill: '#10b981' }} tickFormatter={v => `${v}ms`} />
                <YAxis yAxisId="right" orientation="right" fontSize={9} tick={{ fill: '#ef4444' }} tickFormatter={v => formatSeconds(v)} />
                <Tooltip content={<LatencyTooltip />} />
                <Legend wrapperStyle={{ fontSize: '10px' }} />
                <Bar  yAxisId="left"  dataKey="avg_nuestra" fill="#10b981" radius={[3,3,0,0]} name="WMS (ms)" />
                <Line yAxisId="right" type="monotone" dataKey="avg_erp" stroke="#ef4444" strokeWidth={2.5} dot={{ r: 3 }} name="ERP (seg)" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '4px' }}>
          <div style={{ padding: '10px 12px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <PieChartIcon size={14} color="#2563eb" />
            <h3 style={{ margin: 0, fontSize: '12px', fontWeight: '600', color: '#334155' }}>Latencia por Tipo de Operación</h3>
          </div>
          <div style={{ padding: '12px' }}>
            {tipoOps.filter(t => t !== 'todos').map(tipo => {
              const ops  = DEMO_LATENCY_DATA.filter(d => d.tipo === tipo)
              const avg  = ops.length > 0 ? Math.round(ops.reduce((s, d) => s + d.latencia_nuestra, 0) / ops.length) : 0
              const avgE = ops.length > 0 ? Math.round(ops.reduce((s, d) => s + d.latencia_erp, 0) / ops.length) : 0
              return (
                <div key={tipo} style={{ marginBottom: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                    <span style={{ fontSize: '10px', fontWeight: '600', color: '#334155', textTransform: 'capitalize' }}>
                      {TIPO_ICONS[tipo]} {tipo} ({ops.length} ops)
                    </span>
                    <div style={{ display: 'flex', gap: '10px', fontSize: '9px' }}>
                      <span style={{ color: '#10b981', fontWeight: '700' }}>{avg}ms</span>
                      <span style={{ color: '#94a3b8' }}>vs</span>
                      <span style={{ color: '#ef4444', fontWeight: '700' }}>{formatSeconds(avgE)}</span>
                    </div>
                  </div>
                  <div style={{ flex: 1, height: '6px', background: '#f1f5f9', borderRadius: '3px', overflow: 'hidden' }}>
                    <div style={{ width: `${Math.min((avg / 120) * 100, 100)}%`, height: '100%', background: TIPO_COLORS[tipo], borderRadius: '3px' }} />
                  </div>
                </div>
              )
            })}
            <div style={{ marginTop: '12px', padding: '8px', background: '#f0fdf4', borderRadius: '4px', fontSize: '10px', color: '#15803d' }}>
              <strong>✓ Promedio global:</strong> {avgNuestra}ms nuestra vs {formatSeconds(avgERP)} ERP — <strong>{mejora}x</strong> más rápido
            </div>
          </div>
        </div>
      </div>

      <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '4px' }}>
        <div style={{ padding: '10px 12px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Activity size={14} color="#2563eb" />
            <h3 style={{ margin: 0, fontSize: '12px', fontWeight: '600', color: '#334155' }}>Registro de Operaciones</h3>
          </div>
          <div style={{ display: 'flex', gap: '4px' }}>
            {tipoOps.map(t => (
              <button key={t} onClick={() => setSelectedTipo(t)} style={{ padding: '3px 8px', background: selectedTipo === t ? '#2563eb' : 'white', color: selectedTipo === t ? 'white' : '#64748b', border: '1px solid #e2e8f0', borderRadius: '3px', fontSize: '10px', cursor: 'pointer', textTransform: 'capitalize' }}>
                {t}
              </button>
            ))}
          </div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                {['Fecha/Hora', 'Tipo', 'Código', 'Producto', 'Uds', 'Latencia WMS', 'Latencia ERP', 'Mejora'].map((h, i) => (
                  <th key={i} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: '600', color: '#64748b', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredData.map((op, idx) => {
                const mejX = (op.latencia_erp / op.latencia_nuestra).toFixed(0)
                const TC = { entrada: '#10b981', salida: '#3b82f6', ajuste: '#f59e0b', movimiento: '#8b5cf6' }
                const TI = { entrada: '📦', salida: '📤', ajuste: '⚙️', movimiento: '🔄' }
                return (
                  <tr key={idx} style={{ borderBottom: '1px solid #f8fafc', background: idx % 2 === 0 ? 'white' : '#fafafa' }}>
                    <td style={{ padding: '7px 10px', color: '#64748b', fontSize: '10px', whiteSpace: 'nowrap' }}>{new Date(op.timestamp).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</td>
                    <td style={{ padding: '7px 10px' }}>
                      <span style={{ padding: '2px 7px', background: `${TC[op.tipo]}20`, color: TC[op.tipo], borderRadius: '10px', fontSize: '10px', fontWeight: '600', textTransform: 'capitalize' }}>
                        {TI[op.tipo]} {op.tipo}
                      </span>
                    </td>
                    <td style={{ padding: '7px 10px' }}>
                      <code style={{ background: '#f1f5f9', padding: '1px 5px', borderRadius: '3px', fontSize: '10px' }}>{op.codigo}</code>
                    </td>
                    <td style={{ padding: '7px 10px', fontWeight: '500', color: '#1e293b', maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{op.producto}</td>
                    <td style={{ padding: '7px 10px', color: '#334155', fontWeight: '600' }}>{Math.abs(op.unidades)}</td>
                    <td style={{ padding: '7px 10px' }}><span style={{ color: '#10b981', fontWeight: '700', fontSize: '11px' }}>{op.latencia_nuestra}ms</span></td>
                    <td style={{ padding: '7px 10px' }}><span style={{ color: '#ef4444', fontWeight: '700', fontSize: '11px' }}>{formatSeconds(op.latencia_erp)}</span></td>
                    <td style={{ padding: '7px 10px' }}>
                      <span style={{ padding: '2px 8px', background: '#dbeafe', color: '#1d4ed8', borderRadius: '10px', fontSize: '10px', fontWeight: '700' }}>{mejX}x</span>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

// ============================================
// Panel: Comparación Stock Plataforma vs ERP
// ============================================
const StockERPPanel = ({ stats, productAnalysis, stockData, stockLoading, stockError, onRefresh }) => {
  const [filtroTexto, setFiltroTexto]           = useState('')
  const [filtroZona, setFiltroZona]             = useState('todas')
  const [filtroDiscrepancia, setFiltroDiscrepancia] = useState('todas')
  const [sortBy, setSortBy]   = useState('diff_abs')
  const [sortDir, setSortDir] = useState('desc')
  const [expandido, setExpandido] = useState(null)

  const stockItems = stockData?.productos || []

  if (stockLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '60px' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: '36px', height: '36px', border: '3px solid #e2e8f0', borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 12px' }} />
          <p style={{ color: '#64748b', fontSize: '12px' }}>Consultando stock ERP...</p>
        </div>
      </div>
    )
  }

  if (stockError) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px' }}>
        <div style={{ textAlign: 'center' }}>
          <AlertTriangle size={32} color="#ef4444" style={{ marginBottom: '8px' }} />
          <p style={{ color: '#1e293b', fontSize: '13px', fontWeight: '600', margin: '0 0 4px' }}>Error al cargar stock ERP</p>
          <p style={{ color: '#64748b', fontSize: '11px', margin: '0 0 12px' }}>{stockError}</p>
          {onRefresh && <button onClick={onRefresh} style={{ padding: '6px 14px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '4px', fontSize: '11px', cursor: 'pointer' }}>Reintentar</button>}
        </div>
      </div>
    )
  }

  if (!stockItems.length) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px' }}>
        <div style={{ textAlign: 'center' }}>
          <Database size={32} color="#94a3b8" style={{ marginBottom: '8px' }} />
          <p style={{ color: '#64748b', fontSize: '12px' }}>No hay datos de comparaci&oacute;n disponibles.</p>
          {onRefresh && <button onClick={onRefresh} style={{ padding: '6px 14px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '4px', fontSize: '11px', cursor: 'pointer', marginTop: '8px' }}>Cargar datos</button>}
        </div>
      </div>
    )
  }

  const categorias = ['todas', ...Array.from(new Set(stockItems.map(p => p.categoria).filter(Boolean))).sort()]

  const filtered = stockItems.filter(p => {
    const stockErpNum = typeof p.stock_erp === 'number' ? p.stock_erp : 0
    const diff = Math.abs(parseFloat(calcDiff(p.stock_plataforma, stockErpNum)))
    if (filtroTexto && !p.descripcion.toLowerCase().includes(filtroTexto.toLowerCase()) && !p.codigo.toLowerCase().includes(filtroTexto.toLowerCase())) return false
    if (filtroZona !== 'todas' && p.categoria !== filtroZona) return false
    if (filtroDiscrepancia === 'sin_discrepancia' && diff !== 0) return false
    if (filtroDiscrepancia === 'leve' && (diff === 0 || diff > 2)) return false
    if (filtroDiscrepancia === 'moderada' && (diff <= 2 || diff > 5)) return false
    if (filtroDiscrepancia === 'critica' && diff <= 5) return false
    return true
  }).map(p => {
    const stockErpNum = typeof p.stock_erp === 'number' ? p.stock_erp : 0
    return {
      ...p,
      stock_erp_num: stockErpNum,
      diff_pct: parseFloat(calcDiff(p.stock_plataforma, stockErpNum)),
      diff_abs: p.stock_plataforma - stockErpNum,
      valor_diferencia: Math.abs(p.stock_plataforma - stockErpNum) * (p.precio || 0)
    }
  }).sort((a, b) => {
    const factor = sortDir === 'desc' ? -1 : 1
    if (sortBy === 'diff_abs') return factor * (Math.abs(a.diff_pct) - Math.abs(b.diff_pct))
    if (sortBy === 'stock_plataforma') return factor * (a.stock_plataforma - b.stock_plataforma)
    if (sortBy === 'descripcion') return factor * a.descripcion.localeCompare(b.descripcion)
    return 0
  })

  const resumen = stockData?.resumen || {}
  const total      = resumen.total || stockItems.length
  const sinDiscrep = resumen.sinDiscrepancia ?? stockItems.filter(p => p.diferencia === 0).length
  const conDiscrep = resumen.conDiscrepancia ?? (total - sinDiscrep)
  const criticas   = resumen.criticas ?? stockItems.filter(p => Math.abs(p.diferencia_pct || 0) > 5).length
  const valorTotal = parseFloat(resumen.valorTotal) || stockItems.reduce((s, p) => s + (p.valor_diferencia || 0), 0)

  const discrepanciaChartData = stockItems
    .map(p => {
      const stockErpNum = typeof p.stock_erp === 'number' ? p.stock_erp : 0
      return { name: p.codigo, diff: Math.abs(parseFloat(calcDiff(p.stock_plataforma, stockErpNum))), plat: p.stock_plataforma, erp: stockErpNum }
    })
    .filter(p => p.diff > 0)
    .sort((a, b) => b.diff - a.diff)
    .slice(0, 12)

  const handleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === 'desc' ? 'asc' : 'desc')
    else { setSortBy(col); setSortDir('desc') }
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
        {[
          { label: 'Sin Discrepancia', value: sinDiscrep, color: '#10b981', bg: '#f0fdf4', sub: `${((sinDiscrep / total) * 100).toFixed(0)}% sincronizados` },
          { label: 'Con Discrepancia', value: conDiscrep, color: '#f59e0b', bg: '#fefce8', sub: `${total - sinDiscrep} productos afectados` },
          { label: 'Discrepancias Críticas', value: criticas, color: '#ef4444', bg: '#fef2f2', sub: '>5% de diferencia' },
          { label: 'Valor en Riesgo', value: `S/${valorTotal.toFixed(0)}`, color: '#8b5cf6', bg: '#faf5ff', sub: 'diferencia valorada' },
        ].map((k, i) => (
          <div key={i} style={{ flex: 1, minWidth: '120px', padding: '10px 12px', background: k.bg, borderRadius: '6px', border: `1px solid ${k.color}30` }}>
            <div style={{ fontSize: '9px', color: '#64748b', fontWeight: '600', textTransform: 'uppercase', marginBottom: '4px' }}>{k.label}</div>
            <div style={{ fontSize: '20px', fontWeight: '800', color: k.color }}>{k.value}</div>
            <div style={{ fontSize: '9px', color: '#94a3b8', marginTop: '2px' }}>{k.sub}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
        <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '4px' }}>
          <div style={{ padding: '10px 12px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <BarChart3 size={14} color="#2563eb" />
            <h3 style={{ margin: 0, fontSize: '12px', fontWeight: '600', color: '#334155' }}>Top Discrepancias (% diferencia)</h3>
          </div>
          <div style={{ padding: '12px' }}>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={discrepanciaChartData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis type="number" fontSize={9} tick={{ fill: '#94a3b8' }} tickFormatter={v => `${v}%`} />
                <YAxis type="category" dataKey="name" fontSize={8} tick={{ fill: '#64748b' }} width={60} />
                <Tooltip formatter={(v) => `${v}%`} contentStyle={{ fontSize: '11px' }} />
                <Bar dataKey="diff" radius={[0, 3, 3, 0]} name="% Diferencia">
                  {discrepanciaChartData.map((entry, index) => (
                    <Cell key={index} fill={entry.diff > 5 ? '#ef4444' : entry.diff > 2 ? '#f59e0b' : '#10b981'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '4px' }}>
          <div style={{ padding: '10px 12px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <MapPin size={14} color="#2563eb" />
            <h3 style={{ margin: 0, fontSize: '12px', fontWeight: '600', color: '#334155' }}>Stock por Categoría: WMS vs ERP</h3>
          </div>
          <div style={{ padding: '12px' }}>
            {(() => {
              const zonaMap = {}
              stockItems.forEach(p => {
                const cat = p.categoria || 'Sin categoría'
                const stockErpNum = typeof p.stock_erp === 'number' ? p.stock_erp : 0
                if (!zonaMap[cat]) zonaMap[cat] = { zona: cat, plat: 0, erp: 0 }
                zonaMap[cat].plat += p.stock_plataforma
                zonaMap[cat].erp  += stockErpNum
              })
              return (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={Object.values(zonaMap)}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="zona" fontSize={10} tick={{ fill: '#64748b' }} />
                    <YAxis fontSize={10} tick={{ fill: '#94a3b8' }} />
                    <Tooltip contentStyle={{ fontSize: '11px' }} />
                    <Legend wrapperStyle={{ fontSize: '10px' }} />
                    <Bar dataKey="plat" fill="#2563eb" radius={[3,3,0,0]} name="WMS" />
                    <Bar dataKey="erp"  fill="#94a3b8" radius={[3,3,0,0]} name="ERP Cliente" />
                  </BarChart>
                </ResponsiveContainer>
              )
            })()}
          </div>
        </div>
      </div>

      <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '4px' }}>
        <div style={{ padding: '10px 12px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <GitCompare size={14} color="#2563eb" />
            <h3 style={{ margin: 0, fontSize: '12px', fontWeight: '600', color: '#334155' }}>Tabla Comparativa Stock</h3>
            <span style={{ padding: '2px 6px', background: '#f1f5f9', color: '#64748b', borderRadius: '3px', fontSize: '10px' }}>{filtered.length} productos</span>
          </div>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{ position: 'relative' }}>
              <Search size={11} color="#94a3b8" style={{ position: 'absolute', left: '7px', top: '50%', transform: 'translateY(-50%)' }} />
              <input type="text" placeholder="Buscar producto..." value={filtroTexto} onChange={e => setFiltroTexto(e.target.value)}
                style={{ padding: '4px 8px 4px 24px', border: '1px solid #e2e8f0', borderRadius: '3px', fontSize: '11px', width: '150px', outline: 'none' }} />
            </div>
            <select value={filtroZona} onChange={e => setFiltroZona(e.target.value)} style={{ padding: '4px 8px', border: '1px solid #e2e8f0', borderRadius: '3px', fontSize: '11px', color: '#334155', background: 'white', outline: 'none' }}>
              {categorias.map(z => <option key={z} value={z}>{z === 'todas' ? 'Todas las categorías' : z}</option>)}
            </select>
            <select value={filtroDiscrepancia} onChange={e => setFiltroDiscrepancia(e.target.value)} style={{ padding: '4px 8px', border: '1px solid #e2e8f0', borderRadius: '3px', fontSize: '11px', color: '#334155', background: 'white', outline: 'none' }}>
              <option value="todas">Todas</option>
              <option value="sin_discrepancia">Sin discrepancia</option>
              <option value="leve">Leve (&lt;2%)</option>
              <option value="moderada">Moderada (2-5%)</option>
              <option value="critica">Crítica (&gt;5%)</option>
            </select>
          </div>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #f1f5f9', background: '#f8fafc' }}>
                {[
                  { label: 'Código', key: null },
                  { label: 'Descripción', key: 'descripcion' },
                  { label: 'Categoría', key: null },
                  { label: 'Stock Plataforma', key: 'stock_plataforma' },
                  { label: 'Stock ERP', key: null },
                  { label: 'Diferencia', key: 'diff_abs' },
                  { label: '% Dif.', key: 'diff_abs' },
                  { label: 'Valor S/', key: null },
                ].map((h, i) => (
                  <th key={i} onClick={() => h.key && handleSort(h.key)}
                    style={{ padding: '8px 10px', textAlign: 'left', fontWeight: '600', color: '#64748b', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px', cursor: h.key ? 'pointer' : 'default', whiteSpace: 'nowrap', userSelect: 'none' }}>
                    {h.label} {h.key && sortBy === h.key && (sortDir === 'desc' ? <ChevronDown size={10} /> : <ChevronUp size={10} />)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((p, idx) => {
                const diffPct = p.diff_pct
                const diffAbs = p.diff_abs
                const absDiff = Math.abs(diffPct)
                const color   = getDiffColor(diffPct)
                const isExpand = expandido === p.codigo
                return (
                  <tr key={p.codigo} onClick={() => setExpandido(isExpand ? null : p.codigo)}
                    style={{ borderBottom: '1px solid #f8fafc', background: isExpand ? '#f0fdf4' : (idx % 2 === 0 ? 'white' : '#fafafa'), cursor: 'pointer' }}>
                    <td style={{ padding: '8px 10px' }}>
                      <code style={{ background: '#f1f5f9', padding: '2px 6px', borderRadius: '3px', fontSize: '10px', fontWeight: '600' }}>{p.codigo}</code>
                    </td>
                    <td style={{ padding: '8px 10px', fontWeight: '500', color: '#1e293b', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.descripcion}</td>
                    <td style={{ padding: '8px 10px', fontSize: '10px', color: '#64748b' }}>
                      <span style={{ background: '#dbeafe', color: '#1d4ed8', padding: '1px 5px', borderRadius: '3px', fontWeight: '600', fontSize: '10px' }}>{p.categoria || '-'}</span>
                    </td>
                    <td style={{ padding: '8px 10px', fontWeight: '700', color: '#2563eb' }}>{p.stock_plataforma.toLocaleString()}</td>
                    <td style={{ padding: '8px 10px', fontWeight: '700', color: '#64748b' }}>{typeof p.stock_erp === 'number' ? p.stock_erp_num.toLocaleString() : 'N/A'}</td>
                    <td style={{ padding: '8px 10px' }}>
                      {diffAbs === 0
                        ? <span style={{ color: '#10b981', fontWeight: '700', fontSize: '11px' }}>= 0</span>
                        : <span style={{ color: diffAbs > 0 ? '#2563eb' : '#ef4444', fontWeight: '700', fontSize: '11px' }}>{diffAbs > 0 ? '+' : ''}{diffAbs}</span>
                      }
                    </td>
                    <td style={{ padding: '8px 10px' }}>
                      {absDiff === 0
                        ? <span style={{ padding: '2px 8px', background: '#dcfce7', color: '#15803d', borderRadius: '10px', fontSize: '10px', fontWeight: '700' }}>✓ 0%</span>
                        : <span style={{ padding: '2px 8px', background: `${color}15`, color, borderRadius: '10px', fontSize: '10px', fontWeight: '700' }}>{diffPct > 0 ? '+' : ''}{diffPct}%</span>
                      }
                    </td>
                    <td style={{ padding: '8px 10px', fontSize: '10px', color: absDiff === 0 ? '#10b981' : '#ef4444', fontWeight: '600' }}>
                      {absDiff === 0 ? 'S/0.00' : `S/${p.valor_diferencia.toFixed(2)}`}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        <div style={{ padding: '10px 12px', borderTop: '1px solid #f1f5f9', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '10px', color: '#64748b', fontWeight: '500' }}>Leyenda:</span>
          {[
            { color: '#10b981', label: 'Sin discrepancia (0%)' },
            { color: '#f59e0b', label: 'Leve (<2%)' },
            { color: '#ef4444', label: 'Moderada (2-5%)' },
            { color: '#dc2626', label: 'Crítica (>5%)' },
          ].map((l, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '10px', color: '#64748b' }}>
              <span style={{ width: '10px', height: '10px', background: l.color, borderRadius: '50%', display: 'inline-block' }} />
              {l.label}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ============================================
// COMPONENTE PRINCIPAL ANALYTICS
// ============================================
const Analytics = ({ token }) => {
  const today   = new Date().toISOString().split('T')[0]
  const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]

  const [startDate, setStartDate] = useState(weekAgo)
  const [endDate,   setEndDate]   = useState(today)
  const [loading,   setLoading]   = useState(true)
  const [error,     setError]     = useState(null)
  const [stats,               setStats]               = useState(null)
  const [trends,              setTrends]              = useState(null)
  const [productAnalysis,     setProductAnalysis]     = useState(null)
  const [warehouseAnalysis,   setWarehouseAnalysis]   = useState(null)
  const [supplierAnalysis,    setSupplierAnalysis]    = useState(null)
  const [financialAnalysis,   setFinancialAnalysis]   = useState(null)
  // eslint-disable-next-line no-unused-vars
  const [expiredProducts,    setExpiredProducts]     = useState(null)
  const [kpisData,            setKpisData]            = useState(null)
  const [filtroCodigoPedido,  setFiltroCodigoPedido]  = useState('')
  const [showLegendModal,     setShowLegendModal]     = useState(false)
  const [monitoringCurrent,   setMonitoringCurrent]   = useState(null)
  const [monitoringHistory,   setMonitoringHistory]   = useState(null)
  const [downtimeData,        setDowntimeData]        = useState(null)
  const [endpointStats,       setEndpointStats]       = useState(null)
  const [errorStats,          setErrorStats]          = useState(null)
  const [activeTab, setActiveTab] = useState('business')
  const [erpSubTab, setErpSubTab] = useState('latencia')
  const [erpStockData, setErpStockData]       = useState(null)
  const [erpStockLoading, setErpStockLoading] = useState(false)
  const [erpStockError, setErpStockError]     = useState(null)
  const [erpStockFetched, setErpStockFetched] = useState(false)

  const [activityChartType,  setActivityChartType]  = useState('line')
  const [historicChartType,  setHistoricChartType]  = useState('area')
  const [categoryChartType,  setCategoryChartType]  = useState('pie')
  const [ordersChartType,    setOrdersChartType]    = useState('bar')
  const [zoneChartType,      setZoneChartType]      = useState('bar')
  const [supplierChartType,  setSupplierChartType]  = useState('bar')

  const fetchErpStockData = async () => {
    try {
      setErpStockLoading(true); setErpStockError(null)
      const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      const res = await authFetch(`${API_URL}/analytics/erp/stock-comparison`, { headers })
      if (!res || !res.ok) throw new Error('Error al consultar stock ERP')
      const json = await res.json()
      if (!json.success) throw new Error(json.error || 'Error desconocido')
      setErpStockData(json.data)
      setErpStockFetched(true)
    } catch (err) {
      console.error('Error fetching ERP stock:', err)
      setErpStockError(err.message)
    } finally {
      setErpStockLoading(false)
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (activeTab === 'business') fetchAnalytics()
    else if (activeTab === 'system') fetchMonitoringData()
    else if (activeTab === 'erp' && erpSubTab === 'stock' && !erpStockFetched) fetchErpStockData()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, activeTab, erpSubTab])

  const fetchAnalytics = async () => {
    try {
      setLoading(true); setError(null)
      const params = new URLSearchParams()
      if (startDate) params.append('startDate', startDate)
      if (endDate)   params.append('endDate',   endDate)
      if (filtroCodigoPedido.trim()) params.append('codigo_pedido', filtroCodigoPedido.trim())
      const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      const [statsRes, trendsRes, productsRes, warehouseRes, suppliersRes, financialRes, expiredRes, kpisRes] = await Promise.all([
        authFetch(`${API_URL}/analytics/stats?${params}`,     { headers }),
        authFetch(`${API_URL}/analytics/trends?${params}`,    { headers }),
        authFetch(`${API_URL}/analytics/products`,             { headers }),
        authFetch(`${API_URL}/analytics/warehouse`,            { headers }),
        authFetch(`${API_URL}/analytics/suppliers`,            { headers }),
        authFetch(`${API_URL}/analytics/financial?${params}`, { headers }),
        authFetch(`${API_URL}/analytics/expired-products`,     { headers }),
        authFetch(`${API_URL}/analytics/kpis?${params}`,      { headers }),
      ])
      if (!statsRes || !trendsRes || !productsRes || !warehouseRes || !suppliersRes || !financialRes || !expiredRes || !kpisRes) return;
      if (!statsRes.ok) throw new Error('Error al cargar estadísticas')
      const [sd, td, pd, wd, spd, fd, ed, kd] = await Promise.all([
        statsRes.json(), trendsRes.json(), productsRes.json(), warehouseRes.json(),
        suppliersRes.json(), financialRes.json(), expiredRes.json(), kpisRes.json()
      ])
      setStats(sd.data || null)
      setTrends(td.data || { daily: [], hourly: [] })
      setProductAnalysis(pd.data || { topProducts: [], criticalStock: [], expiring: [] })
      setWarehouseAnalysis(wd.data || { byZone: [], byType: { picking: {}, almacenaje: {} }, summary: {} })
      setSupplierAnalysis(spd.data || [])
      setFinancialAnalysis(fd.data || null)
      setExpiredProducts(ed.data || { total: 0, totalUnits: 0, products: [] })
      setKpisData(kd)
    } catch (error) {
      console.error('Error fetching analytics:', error)
      setError(error.message)
    } finally { setLoading(false) }
  }

  const fetchMonitoringData = async () => {
    try {
      setLoading(true); setError(null)
      const params = new URLSearchParams()
      if (startDate) params.append('startDate', startDate)
      if (endDate)   params.append('endDate',   endDate)
      const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      const [currentRes, historyRes, downtimeRes, endpointsRes, errorsRes] = await Promise.all([
        authFetch(`${API_URL}/analytics/monitoring/current`,            { headers }),
        authFetch(`${API_URL}/analytics/monitoring/history?${params}`,  { headers }),
        authFetch(`${API_URL}/analytics/monitoring/downtime?${params}`, { headers }),
        authFetch(`${API_URL}/analytics/monitoring/endpoints?${params}`,{ headers }),
        authFetch(`${API_URL}/analytics/monitoring/errors?${params}`,   { headers }),
      ])
      if (!currentRes || !historyRes || !downtimeRes || !endpointsRes || !errorsRes) return;
      const [cd, hd, dd, ed2, err] = await Promise.all([
        currentRes.json(), historyRes.json(), downtimeRes.json(), endpointsRes.json(), errorsRes.json()
      ])
      setMonitoringCurrent(cd.data   || null)
      setMonitoringHistory(hd.data   || [])
      setDowntimeData(dd.data         || null)
      setEndpointStats(ed2.data       || [])
      setErrorStats(err.data          || null)
    } catch (error) {
      console.error('Error fetching monitoring data:', error)
      setError(error.message)
    } finally { setLoading(false) }
  }

  const handleApplyFilter = () => fetchAnalytics()
  const handleClearFilter = () => { setFiltroCodigoPedido(''); setTimeout(() => fetchAnalytics(), 100) }

  const getKpiColor = (kpi) => {
    if (kpi >= 9) return '#10B981'; if (kpi >= 8) return '#6EE7B7'; if (kpi >= 7) return '#A7F3D0'
    if (kpi >= 6) return '#FCD34D'; if (kpi >= 5) return '#FBBF24'; if (kpi >= 4) return '#F59E0B'
    if (kpi >= 3) return '#FB923C'; if (kpi >= 2) return '#F87171'; return '#EF4444'
  }

  const formatUptime = (ms) => {
    const seconds = Math.floor(ms / 1000); const minutes = Math.floor(seconds / 60)
    const hours   = Math.floor(minutes / 60); const days = Math.floor(hours / 24)
    if (days > 0)    return `${days}d ${hours % 24}h`
    if (hours > 0)   return `${hours}h ${minutes % 60}m`
    if (minutes > 0) return `${minutes}m`
    return `${seconds}s`
  }

  const CustomKpiTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null
    const data = payload[0].payload
    return (
      <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', maxWidth: '350px', fontSize: '11px' }}>
        <p style={{ margin: '0 0 8px 0', fontWeight: '600', color: '#1e293b', fontSize: '12px' }}>{data.fecha}</p>
        <p style={{ margin: '0 0 6px 0', display: 'flex', alignItems: 'center', gap: '6px' }}>
          KPI Promedio: <strong style={{ color: getKpiColor(data.kpi_promedio), fontSize: '13px' }}>{data.kpi_promedio?.toFixed(2)}</strong>
        </p>
        <p style={{ margin: '0 0 10px 0', color: '#64748b', fontSize: '10px' }}>Total: {data.total_procesos} procesos</p>
        {data.detalles && data.detalles.length > 0 && (
          <div>
            <div style={{ display: 'flex', gap: '12px', marginBottom: '10px', paddingBottom: '8px', borderBottom: '1px solid #f1f5f9', fontSize: '10px' }}>
              <span>📦 Entradas: <strong>{data.entradas.total}</strong></span>
              <span>📤 Salidas: <strong>{data.salidas.total}</strong></span>
              <span>🔄 Retornos: <strong>{data.retornos.total}</strong></span>
            </div>
            <div style={{ maxHeight: '200px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {data.detalles.map((detalle, idx) => (
                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px', background: '#f8fafc', borderRadius: '4px', fontSize: '10px' }}>
                  <span style={{ fontWeight: '600', color: '#334155', minWidth: '70px' }}>{detalle.codigo}</span>
                  <span style={{ backgroundColor: getKpiColor(detalle.kpi), color: 'white', padding: '2px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 'bold', minWidth: '24px', textAlign: 'center' }}>{detalle.kpi}</span>
                  <span style={{ color: '#64748b', flex: 1 }}>{detalle.etiqueta}</span>
                  <span style={{ color: '#94a3b8', fontSize: '9px' }}>({detalle.duracion_formateada})</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    )
  }

  if (loading && !stats && !monitoringCurrent && activeTab !== 'erp') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '60px', minHeight: '100vh', background: '#fafafa' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: '48px', height: '48px', border: '4px solid #e2e8f0', borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
          <p style={{ color: '#64748b', fontSize: '14px' }}>Cargando análisis...</p>
        </div>
      </div>
    )
  }

  if (error && activeTab !== 'erp') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '60px', minHeight: '100vh', background: '#fafafa' }}>
        <div style={{ textAlign: 'center' }}>
          <AlertTriangle size={48} color="#ef4444" style={{ marginBottom: '16px' }} />
          <h3 style={{ margin: '0 0 8px', fontSize: '18px', color: '#1e293b' }}>Error al cargar estadísticas</h3>
          <p style={{ margin: '0 0 16px', fontSize: '13px', color: '#64748b' }}>{error}</p>
          <button onClick={activeTab === 'business' ? fetchAnalytics : fetchMonitoringData} style={{ padding: '8px 16px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '4px', fontSize: '12px', cursor: 'pointer', fontWeight: '500' }}>Reintentar</button>
        </div>
      </div>
    )
  }

  const financialActivityData = financialAnalysis?.porDia ? (() => {
    const entradasMap = {}, salidasMap = {}, allDates = new Set()
    financialAnalysis.porDia.entradas.forEach(e => { entradasMap[e.fecha] = e; allDates.add(e.fecha) })
    financialAnalysis.porDia.salidas.forEach(s  => { salidasMap[s.fecha]  = s; allDates.add(s.fecha) })
    const sortedDates = Array.from(allDates).sort((a, b) => a.localeCompare(b))
    let balanceAcumulado = 0
    return sortedDates.map(fecha => {
      const entrada = entradasMap[fecha] || { monto: 0, valor: 0 }
      const salida  = salidasMap[fecha]  || { monto: 0, costo: 0 }
      const compras = parseFloat(entrada.monto) || 0
      const ventas  = parseFloat(salida.monto)  || 0
      const costos  = parseFloat(salida.costo)  || 0
      balanceAcumulado += (ventas - compras)
      return { fecha, ventas: parseFloat(ventas.toFixed(2)), costos: parseFloat(costos.toFixed(2)), compras: parseFloat(compras.toFixed(2)), balance: parseFloat(balanceAcumulado.toFixed(2)) }
    })
  })() : []

  const categoryData = stats?.inventory?.byCategory
    ? Object.entries(stats.inventory.byCategory).map(([name, data]) => ({ name, productos: data.count || 0, stock: data.stock || 0, valor: parseFloat(data.value) || 0 }))
    : []

  const ordersByStatus = stats ? [
    { name: "Pendiente",       value: stats.orders?.pendiente || 0 },
    { name: "En Preparación", value: stats.orders?.en_preparacion || 0 },
    { name: "Completado",      value: stats.orders?.completado || 0 },
    { name: "Enviado",         value: stats.orders?.enviado || 0 },
  ].filter(item => item.value > 0) : []

  const returnsByReason = stats?.returns?.byReason
    ? Object.entries(stats.returns.byReason).map(([reason, count]) => ({ name: reason, value: count }))
    : []

  const ExportButtons = ({ startDate, endDate }) => {
    const [exporting, setExporting] = useState(null)
    const handleExport = async (format) => {
      try {
        setExporting(format)
        const params = new URLSearchParams()
        if (startDate) params.append('startDate', startDate)
        if (endDate)   params.append('endDate',   endDate)
        const response = await authFetch(`${API_URL}/analytics/export/${format}?${params}`, { headers: { 'Authorization': `Bearer ${token}` } })
        if (!response) return;
        if (!response.ok) throw new Error('Error al exportar')
        const blob = await response.blob()
        const url  = window.URL.createObjectURL(blob)
        const a    = document.createElement('a')
        a.href     = url
        const extension = format === 'excel' ? 'xlsx' : format
        a.download = `analytics_${startDate}_${endDate}.${extension}`
        document.body.appendChild(a); a.click()
        window.URL.revokeObjectURL(url); document.body.removeChild(a)
      } catch (error) {
        console.error('Error exporting:', error); alert('Error al exportar el informe')
      } finally { setExporting(null) }
    }
    const exportOptions = [
      { format: 'csv',   label: 'CSV',   icon: FileText,        color: '#10B981' },
      { format: 'excel', label: 'Excel', icon: FileSpreadsheet, color: '#059669' },
      { format: 'pdf',   label: 'PDF',   icon: FileDown,        color: '#EF4444' }
    ]
    return (
      <div style={{ display: 'flex', gap: '6px' }}>
        {exportOptions.map(({ format, label, icon: Icon, color }) => (
          <button key={format} onClick={() => handleExport(format)} disabled={exporting !== null}
            style={{ padding: '6px 12px', background: exporting === format ? '#f1f5f9' : 'white', border: '1px solid #e2e8f0', borderRadius: '4px', cursor: exporting ? 'not-allowed' : 'pointer', fontSize: '11px', fontWeight: '500', color: '#334155', display: 'flex', alignItems: 'center', gap: '6px', opacity: exporting && exporting !== format ? 0.5 : 1 }}
            onMouseEnter={(e) => { if (!exporting) { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = color } }}
            onMouseLeave={(e) => { if (!exporting) { e.currentTarget.style.background = 'white'; e.currentTarget.style.borderColor = '#e2e8f0' } }}>
            <Icon size={14} color={color} />
            {exporting === format ? 'Exportando...' : label}
          </button>
        ))}
      </div>
    )
  }

  const COLORS = ["#3B82F6", "#10B981", "#F59E0B", "#8B5CF6", "#EF4444", "#06B6D4", "#EC4899", "#14B8A6"]

  return (
    <div style={{ minHeight: '100vh', background: '#fafafa', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      {/* ─── HEADER ─── */}
      <header style={{ background: 'white', borderBottom: '1px solid #e2e8f0', padding: '12px 20px', position: 'sticky', top: 0, zIndex: 100 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <BarChart3 size={20} color="#2563eb" />
            <div>
              <h2 style={{ margin: 0, fontSize: '15px', fontWeight: '600', color: '#1e293b' }}>Analytics</h2>
              <p style={{ margin: 0, fontSize: '11px', color: '#94a3b8' }}>Sistema de métricas y estadísticas</p>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {/* TABS */}
            <div style={{ display: 'flex', gap: '4px', marginRight: '12px' }}>
              {[
                { key: 'business', icon: Package,    label: 'Negocio' },
                { key: 'system',   icon: Server,     label: 'Sistema' },
                { key: 'erp',      icon: GitCompare, label: 'ERP' },
              ].map(tab => (
                <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{ padding: '6px 12px', background: activeTab === tab.key ? '#2563eb' : 'white', color: activeTab === tab.key ? 'white' : '#64748b', border: '1px solid #e2e8f0', borderRadius: '3px', cursor: 'pointer', fontSize: '11px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <tab.icon size={12} />
                  {tab.label}
                  {tab.badge && <span style={{ padding: '1px 4px', background: activeTab === tab.key ? 'rgba(255,255,255,0.3)' : '#f0abfc', color: activeTab === tab.key ? 'white' : '#7e22ce', borderRadius: '3px', fontSize: '8px', fontWeight: '700' }}>{tab.badge}</span>}
                </button>
              ))}
            </div>

            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} max={endDate} style={{ padding: '6px 8px', border: '1px solid #e2e8f0', borderRadius: '3px', fontSize: '11px' }} />
            <span style={{ color: '#cbd5e1' }}>→</span>
            <input type="date" value={endDate}   onChange={(e) => setEndDate(e.target.value)} min={startDate} max={today} style={{ padding: '6px 8px', border: '1px solid #e2e8f0', borderRadius: '3px', fontSize: '11px' }} />

            <div style={{ display: 'flex', gap: '4px' }}>
              {['Hoy', '7d', '30d'].map((label) => (
                <button key={label} onClick={() => {
                  const end = new Date(); const start = new Date(end)
                  if (label === 'Hoy') start.setHours(0, 0, 0, 0)
                  else if (label === '7d') start.setDate(start.getDate() - 7)
                  else start.setMonth(start.getMonth() - 1)
                  setStartDate(start.toISOString().split('T')[0]); setEndDate(end.toISOString().split('T')[0])
                }} style={{ padding: '6px 10px', background: 'white', border: '1px solid #e2e8f0', borderRadius: '3px', cursor: 'pointer', fontSize: '11px', color: '#64748b', fontWeight: '500' }}>
                  {label}
                </button>
              ))}
            </div>

            {activeTab === 'business' && (
              <>
                <div style={{ width: '1px', height: '24px', background: '#e2e8f0', margin: '0 4px' }} />
                <ExportButtons startDate={startDate} endDate={endDate} />
              </>
            )}
          </div>
        </div>

        {/* KPI STRIPS */}
        {activeTab === 'business' && stats && (
          <div style={{ display: 'flex', gap: '12px', marginTop: '12px', padding: '8px 0', borderTop: '1px solid #f1f5f9' }}>
            {[
              { icon: Package,       label: 'Productos', value: stats.inventory?.totalProducts || 0,                               color: '#2563eb', help: 'Cantidad total de productos diferentes en tu inventario' },
              { icon: Database,      label: 'Stock',     value: (stats.inventory?.totalStock || 0).toLocaleString(),               color: '#10b981', help: 'Total de unidades en stock (excluye productos vencidos)' },
              { icon: DollarSign,    label: 'Valor',     value: `S/${parseFloat(stats.inventory?.stockValue || 0).toLocaleString()}`, color: '#8b5cf6', help: 'Valor de venta del inventario actual (precio × stock)' },
              { icon: AlertTriangle, label: 'Bajo',      value: stats.inventory?.lowStockProducts || 0,                           color: '#f59e0b', help: 'Productos con stock por debajo del mínimo configurado' }
            ].map((kpi, idx) => (
              <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 12px', background: '#f8fafc', borderRadius: '4px', border: '1px solid #f1f5f9' }}>
                <kpi.icon size={14} color={kpi.color} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '2px', display: 'flex', alignItems: 'center', gap: '4px' }}>{kpi.label}<HelpTooltip text={kpi.help} /></div>
                  <div style={{ fontSize: '13px', fontWeight: '700', color: '#1e293b' }}>{kpi.value}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'system' && monitoringCurrent && (
          <div style={{ display: 'flex', gap: '12px', marginTop: '12px', padding: '8px 0', borderTop: '1px solid #f1f5f9' }}>
            {[
              { icon: Zap,          label: 'Requests',     value: monitoringCurrent.totalRequests.toLocaleString(), color: '#2563eb', help: 'Total de peticiones procesadas' },
              { icon: CheckCircle2, label: 'Success Rate', value: `${monitoringCurrent.successRate}%`,              color: '#10b981', help: 'Porcentaje de peticiones exitosas' },
              { icon: Timer,        label: 'Avg Response', value: `${monitoringCurrent.avgResponseTime}ms`,          color: '#8b5cf6', help: 'Tiempo promedio de respuesta' },
              { icon: Wifi,         label: 'Uptime',       value: formatUptime(monitoringCurrent.uptime),           color: '#10b981', help: 'Tiempo desde el último reinicio' }
            ].map((kpi, idx) => (
              <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 12px', background: '#f8fafc', borderRadius: '4px', border: '1px solid #f1f5f9' }}>
                <kpi.icon size={14} color={kpi.color} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '2px', display: 'flex', alignItems: 'center', gap: '4px' }}>{kpi.label}<HelpTooltip text={kpi.help} /></div>
                  <div style={{ fontSize: '13px', fontWeight: '700', color: '#1e293b' }}>{kpi.value}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'erp' && (
          <div style={{ display: 'flex', gap: '12px', marginTop: '12px', padding: '8px 0', borderTop: '1px solid #f1f5f9', alignItems: 'center' }}>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: '4px' }}>
              <button onClick={() => setErpSubTab('latencia')} style={{ padding: '5px 12px', background: erpSubTab === 'latencia' ? '#2563eb' : 'white', color: erpSubTab === 'latencia' ? 'white' : '#64748b', border: '1px solid #e2e8f0', borderRadius: '3px', cursor: 'pointer', fontSize: '11px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Zap size={11} /> Latencia Tiempo Real
              </button>
              <button onClick={() => setErpSubTab('stock')} style={{ padding: '5px 12px', background: erpSubTab === 'stock' ? '#2563eb' : 'white', color: erpSubTab === 'stock' ? 'white' : '#64748b', border: '1px solid #e2e8f0', borderRadius: '3px', cursor: 'pointer', fontSize: '11px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <GitCompare size={11} /> Stock vs ERP
              </button>
            </div>
          </div>
        )}
      </header>

      {/* ─── CONTENIDO ─── */}
      <div style={{ padding: '16px 20px' }}>

        {/* ══════════════ TAB: BUSINESS ══════════════ */}
        {activeTab === 'business' && stats && (
          <>
            {/* Financial strip */}
            {financialAnalysis?.resumen && (
              <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', padding: '12px', background: 'white', borderRadius: '4px', border: '1px solid #e2e8f0' }}>
                <div style={{ flex: 1, padding: '8px 12px', background: '#f0fdf4', borderRadius: '4px', borderLeft: '3px solid #10b981' }}>
                  <div style={{ fontSize: '10px', color: '#15803d', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>GANANCIA DE PEDIDOS<HelpTooltip text="Beneficio neto de tus ventas (Ventas - Costo de lo vendido)" /></div>
                  <div style={{ fontSize: '18px', fontWeight: '700', color: '#15803d' }}>S/{parseFloat(financialAnalysis.resumen.gananciaReal || 0).toLocaleString()}</div>
                  <div style={{ fontSize: '10px', color: '#4ade80', marginTop: '2px' }}>Margen {financialAnalysis.resumen.margenGanancia || 0}%</div>
                </div>
                {[
                  { label: 'Ventas',         value: financialAnalysis.resumen.totalVentas,    sub: `${financialAnalysis.resumen.unidadesVendidas || 0} uds`,  color: '#3b82f6', help: 'Ingresos totales por ventas' },
                  { label: 'Costos',         value: financialAnalysis.resumen.costoVentas,    sub: 'de lo vendido',                                            color: '#f59e0b', help: 'Costo real de los productos que vendiste' },
                  { label: 'Compras',        value: financialAnalysis.resumen.totalCompras,   sub: `${financialAnalysis.resumen.unidadesCompradas || 0} uds`,  color: '#8b5cf6', help: 'Gasto total en comprar mercancía' },
                  { label: 'Valor Entradas', value: financialAnalysis.resumen.valorEntradas,  sub: 'Stock nuevo',                                               color: '#14b8a6', help: 'Valor de venta del stock que compraste' },
                  { label: 'Balance',        value: financialAnalysis.resumen.balanceGeneral, sub: 'Neto',                                                      color: parseFloat(financialAnalysis.resumen.balanceGeneral || 0) >= 0 ? '#10b981' : '#ef4444', help: 'Diferencia entre ventas y compras' }
                ].map((item, idx) => (
                  <div key={idx} style={{ flex: 0.8, padding: '8px 12px', background: '#f8fafc', borderRadius: '4px' }}>
                    <div style={{ fontSize: '10px', color: '#64748b', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>{item.label.toUpperCase()}<HelpTooltip text={item.help} /></div>
                    <div style={{ fontSize: '16px', fontWeight: '700', color: item.color }}>S/{parseFloat(item.value || 0).toLocaleString()}</div>
                    <div style={{ fontSize: '9px', color: '#94a3b8', marginTop: '2px' }}>{item.sub}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Pérdida por vencidos */}
            {stats.inventory?.expiredValue && parseFloat(stats.inventory.expiredValue) > 0 && (
              <div style={{ padding: '12px', marginBottom: '16px', background: '#fef2f2', borderRadius: '4px', border: '1px solid #fecaca', display: 'flex', alignItems: 'center', gap: '12px' }}>
                <XCircle size={24} color="#dc2626" />
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
                    <span style={{ fontSize: '12px', fontWeight: '600', color: '#991b1b' }}>Pérdida por Productos Vencidos</span>
                    <HelpTooltip text="Valor en costo de productos caducados que siguen en stock" />
                  </div>
                  <div style={{ fontSize: '18px', fontWeight: '700', color: '#dc2626' }}>-S/{parseFloat(stats.inventory.expiredValue || 0).toLocaleString()}</div>
                  <div style={{ fontSize: '10px', color: '#ef4444', marginTop: '2px' }}>{stats.inventory?.expiredProducts || 0} lotes afectados</div>
                </div>
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>

              {/* Actividad financiera */}
              {financialActivityData.length > 0 && (
                <div style={{ gridColumn: '1 / -1', background: 'white', border: '1px solid #e2e8f0', borderRadius: '4px' }}>
                  <div style={{ padding: '10px 12px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <DollarSign size={14} color="#2563eb" />
                      <h3 style={{ margin: 0, fontSize: '12px', fontWeight: '600', color: '#334155' }}>Actividad de Pedidos (S/)</h3>
                      <HelpTooltip text="Evolución de ventas, costos, compras y balance acumulado." />
                    </div>
                    <ChartTypeSelector value={activityChartType} onChange={setActivityChartType} options={[{ value: 'line', label: 'Línea', icon: TrendingUp }, { value: 'area', label: 'Área', icon: Activity }, { value: 'bar', label: 'Barras', icon: BarChart3 }]} />
                  </div>
                  <div style={{ padding: '12px' }}>
                    {activityChartType === 'line' && (
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={financialActivityData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                          <XAxis dataKey="fecha" stroke="#94A3B8" fontSize={10} />
                          <YAxis stroke="#94A3B8" fontSize={10} />
                          <Tooltip contentStyle={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '11px' }} formatter={(value) => `S/${value.toLocaleString()}`} />
                          <Legend wrapperStyle={{ fontSize: '11px' }} />
                          <Line type="monotone" dataKey="ventas"   stroke="#3B82F6" strokeWidth={2.5} dot={{ r: 3 }} name="Ventas" />
                          <Line type="monotone" dataKey="costos"   stroke="#F59E0B" strokeWidth={2}   dot={{ r: 2.5 }} name="Costos" />
                          <Line type="monotone" dataKey="compras"  stroke="#8B5CF6" strokeWidth={2}   dot={{ r: 2.5 }} name="Compras" />
                          <Line type="monotone" dataKey="balance"  stroke="#10B981" strokeWidth={2.5} strokeDasharray="5 5" dot={{ r: 3 }} name="Balance Acumulado" />
                        </LineChart>
                      </ResponsiveContainer>
                    )}
                    {activityChartType === 'area' && (
                      <ResponsiveContainer width="100%" height={300}>
                        <AreaChart data={financialActivityData}>
                          <defs>
                            <linearGradient id="colorVentas" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3} /><stop offset="95%" stopColor="#3B82F6" stopOpacity={0} /></linearGradient>
                            <linearGradient id="colorCostos" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#F59E0B" stopOpacity={0.3} /><stop offset="95%" stopColor="#F59E0B" stopOpacity={0} /></linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                          <XAxis dataKey="fecha" stroke="#94A3B8" fontSize={10} />
                          <YAxis stroke="#94A3B8" fontSize={10} />
                          <Tooltip contentStyle={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '11px' }} formatter={(value) => `S/${value.toLocaleString()}`} />
                          <Legend wrapperStyle={{ fontSize: '11px' }} />
                          <Area type="monotone" dataKey="ventas"  stroke="#3B82F6" fill="url(#colorVentas)" strokeWidth={2} name="Ventas" />
                          <Area type="monotone" dataKey="costos"  stroke="#F59E0B" fill="url(#colorCostos)" strokeWidth={2} name="Costos" />
                          <Line type="monotone" dataKey="balance" stroke="#10B981" strokeWidth={2.5} strokeDasharray="5 5" dot={{ r: 3 }} name="Balance Acumulado" />
                        </AreaChart>
                      </ResponsiveContainer>
                    )}
                    {activityChartType === 'bar' && (
                      <ResponsiveContainer width="100%" height={300}>
                        <ComposedChart data={financialActivityData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                          <XAxis dataKey="fecha" stroke="#94A3B8" fontSize={10} />
                          <YAxis stroke="#94A3B8" fontSize={10} />
                          <Tooltip contentStyle={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '11px' }} formatter={(value) => `S/${value.toLocaleString()}`} />
                          <Legend wrapperStyle={{ fontSize: '11px' }} />
                          <Bar  dataKey="ventas"  fill="#3B82F6" radius={[4,4,0,0]} name="Ventas" />
                          <Bar  dataKey="costos"  fill="#F59E0B" radius={[4,4,0,0]} name="Costos" />
                          <Bar  dataKey="compras" fill="#8B5CF6" radius={[4,4,0,0]} name="Compras" />
                          <Line dataKey="balance" stroke="#10B981" strokeWidth={2.5} dot={{ r: 3 }} name="Balance Acumulado" />
                        </ComposedChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                </div>
              )}

              {/* KPI Rendimiento */}
              {kpisData && kpisData.datos_por_dia && kpisData.datos_por_dia.length > 0 && (
                <div style={{ gridColumn: '1 / -1', background: 'white', border: '1px solid #e2e8f0', borderRadius: '4px' }}>
                  <div style={{ padding: '10px 12px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Activity size={14} color="#2563eb" />
                      <h3 style={{ margin: 0, fontSize: '12px', fontWeight: '600', color: '#334155' }}>Rendimiento Operativo</h3>
                      <HelpTooltip text="KPI de rendimiento basado en tiempos de ejecución de procesos (escala 1-10)." />
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                        <input type="text" placeholder="Buscar pedido..." value={filtroCodigoPedido} onChange={(e) => setFiltroCodigoPedido(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && handleApplyFilter()}
                          style={{ padding: '4px 8px', border: '1px solid #e2e8f0', borderRadius: '3px', fontSize: '11px', width: '140px', outline: 'none' }} />
                        <button onClick={handleApplyFilter} style={{ padding: '4px 8px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '11px', display: 'flex', alignItems: 'center' }}><Search size={12} /></button>
                        {filtroCodigoPedido && <button onClick={handleClearFilter} style={{ padding: '4px 8px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '11px', display: 'flex', alignItems: 'center' }}><X size={12} /></button>}
                      </div>
                      <button onClick={() => setShowLegendModal(true)} style={{ padding: '4px 8px', background: 'white', border: '1px solid #e2e8f0', borderRadius: '3px', cursor: 'pointer', fontSize: '11px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Info size={12} />Leyenda
                      </button>
                    </div>
                  </div>
                  <div style={{ padding: '12px' }}>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={kpisData.datos_por_dia}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis dataKey="fecha" tick={{ fontSize: 10 }} tickFormatter={(value) => new Date(value).toLocaleDateString('es-ES', { month: 'short', day: 'numeric' })} />
                        <YAxis domain={[0, 10]} ticks={[0,2,4,6,8,10]} tick={{ fontSize: 10 }} />
                        <Tooltip content={<CustomKpiTooltip />} />
                        <Legend wrapperStyle={{ fontSize: '11px' }} />
                        <Line type="monotone" dataKey="kpi_promedio"          stroke="#667eea" strokeWidth={2.5} name="KPI Promedio"  dot={{ fill: '#667eea', r: 3 }} activeDot={{ r: 5 }} />
                        <Line type="monotone" dataKey="entradas.kpi_promedio" stroke="#10B981" strokeWidth={1.5} strokeDasharray="5 5" name="KPI Entradas" dot={false} />
                        <Line type="monotone" dataKey="salidas.kpi_promedio"  stroke="#F59E0B" strokeWidth={1.5} strokeDasharray="5 5" name="KPI Salidas"  dot={false} />
                        <Line type="monotone" dataKey="retornos.kpi_promedio" stroke="#8B5CF6" strokeWidth={1.5} strokeDasharray="5 5" name="KPI Retornos" dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* Histórico pedidos */}
              {trends?.daily && trends.daily.length > 0 && (
                <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '4px' }}>
                  <div style={{ padding: '10px 12px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Activity size={14} color="#2563eb" />
                      <h3 style={{ margin: 0, fontSize: '12px', fontWeight: '600', color: '#334155' }}>Histórico Nº Pedidos</h3>
                      <HelpTooltip text="Cantidad de pedidos (entradas/salidas) por día" />
                    </div>
                    <ChartTypeSelector value={historicChartType} onChange={setHistoricChartType} options={[{ value: 'area', label: 'Área', icon: Activity }, { value: 'line', label: 'Línea', icon: TrendingUp }, { value: 'bar', label: 'Barras', icon: BarChart3 }]} />
                  </div>
                  <div style={{ padding: '12px' }}>
                    {historicChartType === 'area' && (
                      <ResponsiveContainer width="100%" height={220}>
                        <ComposedChart data={trends.daily}>
                          <defs>
                            <linearGradient id="colorEntradas" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#10B981" stopOpacity={0.3} /><stop offset="95%" stopColor="#10B981" stopOpacity={0} /></linearGradient>
                            <linearGradient id="colorSalidas"  x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3} /><stop offset="95%" stopColor="#3B82F6" stopOpacity={0} /></linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                          <XAxis dataKey="date" stroke="#94A3B8" fontSize={9} />
                          <YAxis stroke="#94A3B8" fontSize={10} />
                          <Tooltip contentStyle={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '11px' }} />
                          <Legend wrapperStyle={{ fontSize: '10px' }} />
                          <Area type="monotone" dataKey="entradas" stroke="#10B981" fill="url(#colorEntradas)" strokeWidth={2} name="Entradas" />
                          <Area type="monotone" dataKey="salidas"  stroke="#3B82F6" fill="url(#colorSalidas)"  strokeWidth={2} name="Salidas" />
                        </ComposedChart>
                      </ResponsiveContainer>
                    )}
                    {historicChartType === 'line' && (
                      <ResponsiveContainer width="100%" height={220}>
                        <LineChart data={trends.daily}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                          <XAxis dataKey="date" stroke="#94A3B8" fontSize={9} />
                          <YAxis stroke="#94A3B8" fontSize={10} />
                          <Tooltip contentStyle={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '11px' }} />
                          <Legend wrapperStyle={{ fontSize: '10px' }} />
                          <Line type="monotone" dataKey="entradas" stroke="#10B981" strokeWidth={2} dot={{ r: 2 }} name="Entradas" />
                          <Line type="monotone" dataKey="salidas"  stroke="#3B82F6" strokeWidth={2} dot={{ r: 2 }} name="Salidas" />
                        </LineChart>
                      </ResponsiveContainer>
                    )}
                    {historicChartType === 'bar' && (
                      <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={trends.daily}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                          <XAxis dataKey="date" stroke="#94A3B8" fontSize={9} />
                          <YAxis stroke="#94A3B8" fontSize={10} />
                          <Tooltip contentStyle={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '11px' }} />
                          <Legend wrapperStyle={{ fontSize: '10px' }} />
                          <Bar dataKey="entradas" fill="#10B981" radius={[4,4,0,0]} name="Entradas" />
                          <Bar dataKey="salidas"  fill="#3B82F6" radius={[4,4,0,0]} name="Salidas" />
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                </div>
              )}

              {/* Pedidos por estado */}
              {ordersByStatus.length > 0 && (
                <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '4px' }}>
                  <div style={{ padding: '10px 12px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <ShoppingCart size={14} color="#2563eb" />
                      <h3 style={{ margin: 0, fontSize: '12px', fontWeight: '600', color: '#334155' }}>Pedidos</h3>
                      <HelpTooltip text="Distribución de pedidos según su estado actual" />
                    </div>
                    <ChartTypeSelector value={ordersChartType} onChange={setOrdersChartType} options={[{ value: 'bar', label: 'Barras', icon: BarChart3 }, { value: 'pie', label: 'Circular', icon: PieChartIcon }]} />
                  </div>
                  <div style={{ padding: '12px' }}>
                    {ordersChartType === 'bar' && (
                      <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={ordersByStatus}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                          <XAxis dataKey="name" stroke="#94A3B8" fontSize={10} />
                          <YAxis stroke="#94A3B8" fontSize={10} />
                          <Tooltip contentStyle={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '11px' }} />
                          <Bar dataKey="value" fill="#3B82F6" radius={[4,4,0,0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                    {ordersChartType === 'pie' && (
                      <ResponsiveContainer width="100%" height={220}>
                        <PieChart>
                          <Pie data={ordersByStatus} cx="50%" cy="50%" outerRadius={75} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                            {ordersByStatus.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                          </Pie>
                          <Tooltip contentStyle={{ fontSize: '11px' }} />
                        </PieChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                </div>
              )}

              {/* ── PREDICCIÓN DE DEMANDA — integrada en el grid, mismo estilo ── */}
              <DemandPredictionPanel token={token} startDate={startDate} endDate={endDate} />

              {/* Top productos */}
              {productAnalysis && productAnalysis.topProducts && productAnalysis.topProducts.length > 0 && (
                <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '4px' }}>
                  <div style={{ padding: '10px 12px', borderBottom: '1px solid #f1f5f9' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <TrendingUp size={14} color="#2563eb" />
                      <h3 style={{ margin: 0, fontSize: '12px', fontWeight: '600', color: '#334155' }}>Top Productos</h3>
                      <HelpTooltip text="Productos con mayor cantidad de stock" />
                    </div>
                  </div>
                  <div style={{ padding: '8px' }}>
                    {productAnalysis.topProducts.slice(0, 8).map((product, idx) => (
                      <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 8px', marginBottom: idx < 7 ? '4px' : 0, background: '#f8fafc', borderRadius: '3px' }}>
                        <div style={{ width: '20px', height: '20px', background: '#3B82F6', color: 'white', borderRadius: '3px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: '700', flexShrink: 0 }}>{idx + 1}</div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '11px', fontWeight: '600', color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{product.descripcion}</div>
                          <div style={{ fontSize: '9px', color: '#94a3b8' }}>{product.codigo}</div>
                        </div>
                        <div style={{ fontSize: '12px', fontWeight: '700', color: '#3B82F6', flexShrink: 0 }}>{(product.stock || 0).toLocaleString()}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Categorías */}
              {categoryData.length > 0 && (
                <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '4px' }}>
                  <div style={{ padding: '10px 12px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Layers size={14} color="#2563eb" />
                      <h3 style={{ margin: 0, fontSize: '12px', fontWeight: '600', color: '#334155' }}>Categorías</h3>
                      <HelpTooltip text="Distribución de stock por categorías de productos" />
                    </div>
                    <ChartTypeSelector value={categoryChartType} onChange={setCategoryChartType} options={[{ value: 'pie', label: 'Circular', icon: PieChartIcon }, { value: 'bar', label: 'Barras', icon: BarChart3 }]} />
                  </div>
                  <div style={{ padding: '12px' }}>
                    {categoryChartType === 'pie' && (
                      <ResponsiveContainer width="100%" height={220}>
                        <PieChart>
                          <Pie data={categoryData} cx="50%" cy="50%" labelLine={false} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} outerRadius={75} dataKey="stock">
                            {categoryData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                          </Pie>
                          <Tooltip contentStyle={{ fontSize: '11px' }} />
                        </PieChart>
                      </ResponsiveContainer>
                    )}
                    {categoryChartType === 'bar' && (
                      <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={categoryData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                          <XAxis dataKey="name" stroke="#94A3B8" fontSize={9} angle={-30} textAnchor="end" height={70} />
                          <YAxis stroke="#94A3B8" fontSize={10} />
                          <Tooltip contentStyle={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '11px' }} />
                          <Bar dataKey="stock" fill="#3B82F6" radius={[4,4,0,0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                </div>
              )}

              {/* Zona Warehouse */}
              {warehouseAnalysis?.byZone && warehouseAnalysis.byZone.length > 0 && (
                <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '4px' }}>
                  <div style={{ padding: '10px 12px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <MapPin size={14} color="#2563eb" />
                      <h3 style={{ margin: 0, fontSize: '12px', fontWeight: '600', color: '#334155' }}>Zonas</h3>
                      <HelpTooltip text="Ocupación de niveles por zona del almacén" />
                    </div>
                    <ChartTypeSelector value={zoneChartType} onChange={setZoneChartType} options={[{ value: 'bar', label: 'Barras', icon: BarChart3 }, { value: 'line', label: 'Línea', icon: TrendingUp }]} />
                  </div>
                  <div style={{ padding: '12px' }}>
                    {zoneChartType === 'bar' && (
                      <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={warehouseAnalysis.byZone}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                          <XAxis dataKey="zona" stroke="#94A3B8" fontSize={10} />
                          <YAxis stroke="#94A3B8" fontSize={10} />
                          <Tooltip contentStyle={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '11px' }} />
                          <Legend wrapperStyle={{ fontSize: '10px' }} />
                          <Bar dataKey="ocupados"    stackId="a" fill="#3B82F6" name="Ocupados" />
                          <Bar dataKey="conVencidos" stackId="a" fill="#EF4444" name="Vencidos" />
                          <Bar dataKey="libres"      stackId="a" fill="#E5E7EB" radius={[4,4,0,0]} name="Libres" />
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                    {zoneChartType === 'line' && (
                      <ResponsiveContainer width="100%" height={220}>
                        <LineChart data={warehouseAnalysis.byZone}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                          <XAxis dataKey="zona" stroke="#94A3B8" fontSize={10} />
                          <YAxis stroke="#94A3B8" fontSize={10} />
                          <Tooltip contentStyle={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '11px' }} />
                          <Legend wrapperStyle={{ fontSize: '10px' }} />
                          <Line type="monotone" dataKey="ocupados"    stroke="#3B82F6" strokeWidth={2}   dot={{ r: 2 }} name="Ocupados" />
                          <Line type="monotone" dataKey="conVencidos" stroke="#EF4444" strokeWidth={2}   dot={{ r: 2 }} name="Vencidos" />
                          <Line type="monotone" dataKey="totalNiveles" stroke="#94A3B8" strokeWidth={1.5} strokeDasharray="4 4" dot={{ r: 1.5 }} name="Total" />
                        </LineChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                </div>
              )}

              {/* Proveedores */}
              {supplierAnalysis && supplierAnalysis.length > 0 && (
                <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '4px' }}>
                  <div style={{ padding: '10px 12px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Truck size={14} color="#2563eb" />
                      <h3 style={{ margin: 0, fontSize: '12px', fontWeight: '600', color: '#334155' }}>Proveedores</h3>
                      <HelpTooltip text="Valor total de stock por proveedor" />
                    </div>
                    <ChartTypeSelector value={supplierChartType} onChange={setSupplierChartType} options={[{ value: 'bar', label: 'Barras', icon: BarChart3 }, { value: 'horizontal', label: 'Horizontal', icon: BarChart3 }, { value: 'line', label: 'Línea', icon: TrendingUp }]} />
                  </div>
                  <div style={{ padding: '12px' }}>
                    {supplierChartType === 'horizontal' && (
                      <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={supplierAnalysis.slice(0, 8)} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                          <XAxis type="number" stroke="#94A3B8" fontSize={10} />
                          <YAxis type="category" dataKey="nombre" stroke="#94A3B8" fontSize={9} width={80} />
                          <Tooltip formatter={(value) => `S/${value.toLocaleString()}`} contentStyle={{ fontSize: '11px' }} />
                          <Bar dataKey="valorTotal" fill="#3B82F6" radius={[0,3,3,0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                    {supplierChartType === 'bar' && (
                      <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={supplierAnalysis.slice(0, 8)}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                          <XAxis dataKey="nombre" stroke="#94A3B8" fontSize={9} angle={-30} textAnchor="end" height={80} />
                          <YAxis stroke="#94A3B8" fontSize={10} />
                          <Tooltip formatter={(value) => `S/${value.toLocaleString()}`} contentStyle={{ fontSize: '11px' }} />
                          <Bar dataKey="valorTotal" fill="#3B82F6" radius={[4,4,0,0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                    {supplierChartType === 'line' && (
                      <ResponsiveContainer width="100%" height={260}>
                        <LineChart data={supplierAnalysis.slice(0, 8)}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                          <XAxis dataKey="nombre" stroke="#94A3B8" fontSize={9} angle={-30} textAnchor="end" height={80} />
                          <YAxis stroke="#94A3B8" fontSize={10} />
                          <Tooltip formatter={(value) => `S/${value.toLocaleString()}`} contentStyle={{ fontSize: '11px' }} />
                          <Line type="monotone" dataKey="valorTotal" stroke="#3B82F6" strokeWidth={2} dot={{ fill: "#3B82F6", r: 3 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                </div>
              )}

              {/* Retornos */}
              {stats.returns && stats.returns.total > 0 && (
                <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '4px' }}>
                  <div style={{ padding: '10px 12px', borderBottom: '1px solid #f1f5f9' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                      <RotateCcw size={14} color="#2563eb" />
                      <h3 style={{ margin: 0, fontSize: '12px', fontWeight: '600', color: '#334155' }}>Retornos</h3>
                      <HelpTooltip text="Productos devueltos y su estado de procesamiento" />
                      <span style={{ marginLeft: 'auto', fontSize: '10px', color: '#64748b' }}>{stats.returns.totalUnits} uds</span>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', fontSize: '10px' }}>
                      {[{ label: 'Total', value: stats.returns.total }, { label: 'Pendiente', value: stats.returns.pendiente || 0 }, { label: 'Procesado', value: stats.returns.procesado || 0 }].map((stat, idx) => (
                        <div key={idx} style={{ flex: 1, textAlign: 'center', padding: '4px', background: '#f8fafc', borderRadius: '3px' }}>
                          <div style={{ fontWeight: '700', color: '#1e293b' }}>{stat.value}</div>
                          <div style={{ color: '#94a3b8' }}>{stat.label}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                  {returnsByReason.length > 0 && (
                    <div style={{ padding: '12px' }}>
                      <ResponsiveContainer width="100%" height={180}>
                        <PieChart>
                          <Pie data={returnsByReason} cx="50%" cy="50%" outerRadius={60} dataKey="value" label={({ percent }) => `${(percent * 100).toFixed(0)}%`}>
                            {returnsByReason.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                          </Pie>
                          <Tooltip contentStyle={{ fontSize: '11px' }} />
                          <Legend wrapperStyle={{ fontSize: '10px' }} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>
              )}

              {/* Stock Crítico */}
              {productAnalysis && productAnalysis.criticalStock && productAnalysis.criticalStock.length > 0 && (
                <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '4px' }}>
                  <div style={{ padding: '10px 12px', borderBottom: '1px solid #f1f5f9' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <AlertTriangle size={14} color="#f59e0b" />
                      <h3 style={{ margin: 0, fontSize: '12px', fontWeight: '600', color: '#334155' }}>Stock Crítico</h3>
                      <HelpTooltip text="Productos con stock por debajo del mínimo" />
                      <span style={{ marginLeft: 'auto', padding: '2px 6px', background: '#fef3c7', color: '#92400e', borderRadius: '3px', fontSize: '9px', fontWeight: '600' }}>{productAnalysis.criticalStock.length}</span>
                    </div>
                  </div>
                  <div style={{ padding: '8px' }}>
                    {productAnalysis.criticalStock.slice(0, 8).map((product, idx) => (
                      <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 8px', marginBottom: idx < 7 ? '4px' : 0, background: '#fef2f2', borderRadius: '3px', border: '1px solid #fecaca' }}>
                        <div style={{ width: '20px', height: '20px', background: '#EF4444', color: 'white', borderRadius: '3px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><AlertTriangle size={12} /></div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '11px', fontWeight: '600', color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{product.descripcion}</div>
                          <div style={{ fontSize: '9px', color: '#94a3b8' }}>{product.codigo}</div>
                        </div>
                        <div style={{ fontSize: '11px', fontWeight: '600', flexShrink: 0 }}>
                          <span style={{ color: '#EF4444' }}>{product.stock || 0}</span>
                          <span style={{ color: '#cbd5e1', margin: '0 2px' }}>/</span>
                          <span style={{ color: '#10B981' }}>{product.min_stock || 0}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Próximos a vencer */}
              {productAnalysis && productAnalysis.expiring && productAnalysis.expiring.length > 0 && (
                <div style={{ gridColumn: '1 / -1', background: 'white', border: '1px solid #e2e8f0', borderRadius: '4px' }}>
                  <div style={{ padding: '10px 12px', borderBottom: '1px solid #f1f5f9' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Clock size={14} color="#f59e0b" />
                      <h3 style={{ margin: 0, fontSize: '12px', fontWeight: '600', color: '#334155' }}>Próximos a Vencer</h3>
                      <HelpTooltip text="Lotes que vencen en los próximos 30 días" />
                      <span style={{ marginLeft: 'auto', padding: '2px 6px', background: '#fef3c7', color: '#92400e', borderRadius: '3px', fontSize: '9px', fontWeight: '600' }}>{productAnalysis.expiring.length} lotes</span>
                    </div>
                  </div>
                  <div style={{ overflowX: 'auto', padding: '0 12px 12px' }}>
                    <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                          {['Código', 'Descripción', 'Lote', 'Vencimiento', 'Stock', 'Días', 'Ubicaciones'].map((header, idx) => (
                            <th key={idx} style={{ padding: '8px', textAlign: 'left', fontWeight: '600', color: '#64748b', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{header}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {productAnalysis.expiring.slice(0, 10).map((item, idx) => (
                          <tr key={idx} style={{ borderBottom: '1px solid #f8fafc', background: item.dias_restantes <= 7 ? '#fef2f2' : 'white' }}>
                            <td style={{ padding: '8px' }}><code style={{ background: '#f1f5f9', padding: '2px 6px', borderRadius: '3px', fontSize: '10px', fontWeight: '600' }}>{item.codigo}</code></td>
                            <td style={{ padding: '8px', fontWeight: '500' }}>{item.descripcion}</td>
                            <td style={{ padding: '8px' }}><span style={{ background: '#dbeafe', color: '#1e40af', padding: '2px 6px', borderRadius: '3px', fontSize: '10px', fontWeight: '600' }}>{item.lote}</span></td>
                            <td style={{ padding: '8px', color: '#64748b' }}>{new Date(item.fecha_vencimiento).toLocaleDateString("es-ES")}</td>
                            <td style={{ padding: '8px', fontWeight: '600' }}>{item.stock} uds</td>
                            <td style={{ padding: '8px' }}><span style={{ padding: '2px 8px', background: item.dias_restantes <= 7 ? '#fee2e2' : '#fef3c7', color: item.dias_restantes <= 7 ? '#dc2626' : '#d97706', borderRadius: '10px', fontSize: '10px', fontWeight: '700' }}>{item.dias_restantes}d</span></td>
                            <td style={{ padding: '8px' }}>
                              <div style={{ display: 'flex', gap: '3px', flexWrap: 'wrap' }}>
                                {item.ubicaciones && item.ubicaciones.slice(0, 3).map((ub, i) => (
                                  <span key={i} style={{ background: '#e0e7ff', color: '#3730a3', padding: '1px 5px', borderRadius: '2px', fontSize: '9px', fontWeight: '600' }}>{ub}</span>
                                ))}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* ══════════════ TAB: SYSTEM MONITORING ══════════════ */}
        {activeTab === 'system' && (
          <>
            {monitoringCurrent && (
              <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', padding: '12px', background: 'white', borderRadius: '4px', border: '1px solid #e2e8f0' }}>
                <div style={{ flex: 1, padding: '12px', background: monitoringCurrent.successRate >= 99 ? '#f0fdf4' : '#fef2f2', borderRadius: '4px', borderLeft: `3px solid ${monitoringCurrent.successRate >= 99 ? '#10b981' : '#ef4444'}` }}>
                  <div style={{ fontSize: '10px', color: monitoringCurrent.successRate >= 99 ? '#15803d' : '#991b1b', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>ESTADO DEL SISTEMA<HelpTooltip text="Porcentaje de peticiones exitosas vs fallidas" /></div>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: monitoringCurrent.successRate >= 99 ? '#15803d' : '#991b1b' }}>
                    {monitoringCurrent.successRate >= 99
                      ? <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Wifi size={24} color="#10b981" />ONLINE</span>
                      : <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><WifiOff size={24} color="#ef4444" />DEGRADADO</span>}
                  </div>
                  <div style={{ fontSize: '10px', color: '#64748b', marginTop: '4px' }}>Success Rate: {monitoringCurrent.successRate}%</div>
                </div>
                {[
                  { label: 'Total Requests', value: monitoringCurrent.totalRequests.toLocaleString(), sub: `${monitoringCurrent.failedRequests} errores`, color: '#3b82f6', help: 'Total de peticiones HTTP procesadas' },
                  { label: 'Avg Response Time', value: `${monitoringCurrent.avgResponseTime}ms`, sub: 'Promedio', color: monitoringCurrent.avgResponseTime < 500 ? '#10b981' : monitoringCurrent.avgResponseTime < 1000 ? '#f59e0b' : '#ef4444', help: 'Tiempo promedio de respuesta del servidor' },
                  { label: 'Uptime', value: formatUptime(monitoringCurrent.uptime), sub: 'Desde inicio', color: '#10b981', help: 'Tiempo que el servidor lleva activo' }
                ].map((item, idx) => (
                  <div key={idx} style={{ flex: 0.8, padding: '12px', background: '#f8fafc', borderRadius: '4px' }}>
                    <div style={{ fontSize: '10px', color: '#64748b', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>{item.label.toUpperCase()}<HelpTooltip text={item.help} /></div>
                    <div style={{ fontSize: '18px', fontWeight: '700', color: item.color }}>{item.value}</div>
                    <div style={{ fontSize: '9px', color: '#94a3b8', marginTop: '2px' }}>{item.sub}</div>
                  </div>
                ))}
              </div>
            )}

            {downtimeData && downtimeData.periods && downtimeData.periods.length > 0 && (
              <div style={{ padding: '12px', marginBottom: '16px', background: '#fef2f2', borderRadius: '4px', border: '1px solid #fecaca', display: 'flex', alignItems: 'center', gap: '12px' }}>
                <WifiOff size={24} color="#dc2626" />
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
                    <span style={{ fontSize: '12px', fontWeight: '600', color: '#991b1b' }}>Incidentes de Downtime Detectados</span>
                    <HelpTooltip text="Períodos donde el sistema no respondió heartbeats" />
                  </div>
                  <div style={{ fontSize: '18px', fontWeight: '700', color: '#dc2626' }}>{downtimeData.incidents_count} incidentes</div>
                  <div style={{ fontSize: '10px', color: '#ef4444', marginTop: '2px' }}>Tiempo total caído: {downtimeData.total_downtime_minutes} minutos</div>
                </div>
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
              {monitoringHistory && monitoringHistory.length > 0 ? (
                <div style={{ gridColumn: '1 / -1', background: 'white', border: '1px solid #e2e8f0', borderRadius: '4px' }}>
                  <div style={{ padding: '10px 12px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Wifi size={14} color="#2563eb" />
                    <h3 style={{ margin: 0, fontSize: '12px', fontWeight: '600', color: '#334155' }}>Disponibilidad del Sistema (Uptime %)</h3>
                    <HelpTooltip text="Porcentaje de uptime por día. 100% = sistema completamente disponible" />
                  </div>
                  <div style={{ padding: '12px' }}>
                    <ResponsiveContainer width="100%" height={300}>
                      <ComposedChart data={monitoringHistory}>
                        <defs><linearGradient id="colorUptime" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#10B981" stopOpacity={0.3} /><stop offset="95%" stopColor="#10B981" stopOpacity={0} /></linearGradient></defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis dataKey="date" stroke="#94A3B8" fontSize={10} tickFormatter={(value) => new Date(value).toLocaleDateString('es-ES', { month: 'short', day: 'numeric' })} />
                        <YAxis stroke="#94A3B8" fontSize={10} domain={[90, 100]} ticks={[90,92,94,96,98,100]} />
                        <Tooltip contentStyle={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '11px' }} formatter={(value, name) => { if (name === 'uptime_percentage') return [`${value}%`, 'Uptime']; if (name === 'downtime_minutes') return [`${value} min`, 'Downtime']; return [value, name] }} />
                        <Legend wrapperStyle={{ fontSize: '11px' }} />
                        <Area type="monotone" dataKey="uptime_percentage" stroke="#10B981" fill="url(#colorUptime)" strokeWidth={3} name="Uptime %" />
                        <Line type="monotone" dataKey="downtime_minutes" stroke="#EF4444" strokeWidth={2} yAxisId="right" name="Downtime (min)" dot={{ r: 2 }} />
                        <YAxis yAxisId="right" orientation="right" stroke="#EF4444" fontSize={10} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              ) : (
                <div style={{ gridColumn: '1 / -1', background: 'white', border: '1px solid #e2e8f0', borderRadius: '4px', padding: '40px', textAlign: 'center' }}>
                  <Server size={40} color="#cbd5e1" style={{ marginBottom: '12px' }} />
                  <h3 style={{ margin: '0 0 8px', fontSize: '14px', color: '#64748b' }}>No hay datos históricos aún</h3>
                  <p style={{ margin: 0, fontSize: '11px', color: '#94a3b8' }}>El sistema está recopilando métricas. Selecciona un rango de fechas mayor.</p>
                </div>
              )}

              {monitoringHistory && monitoringHistory.length > 0 && (
                <div style={{ gridColumn: '1 / -1', background: 'white', border: '1px solid #e2e8f0', borderRadius: '4px' }}>
                  <div style={{ padding: '10px 12px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Timer size={14} color="#2563eb" />
                    <h3 style={{ margin: 0, fontSize: '12px', fontWeight: '600', color: '#334155' }}>Tiempos de Respuesta (Promedio, Máximo, Mínimo)</h3>
                  </div>
                  <div style={{ padding: '12px' }}>
                    <ResponsiveContainer width="100%" height={300}>
                      <ComposedChart data={monitoringHistory}>
                        <defs><linearGradient id="colorAvgTime" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3} /><stop offset="95%" stopColor="#3B82F6" stopOpacity={0} /></linearGradient></defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis dataKey="date" stroke="#94A3B8" fontSize={10} tickFormatter={(value) => new Date(value).toLocaleDateString('es-ES', { month: 'short', day: 'numeric' })} />
                        <YAxis stroke="#94A3B8" fontSize={10} />
                        <Tooltip contentStyle={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '11px' }} formatter={(value) => `${value}ms`} />
                        <Legend wrapperStyle={{ fontSize: '11px' }} />
                        <Area type="monotone" dataKey="avg_response_time" stroke="#3B82F6" fill="url(#colorAvgTime)" strokeWidth={2.5} name="Promedio" />
                        <Line type="monotone" dataKey="max_response_time" stroke="#EF4444" strokeWidth={2} strokeDasharray="5 5" name="Máximo" dot={false} />
                        <Line type="monotone" dataKey="min_response_time" stroke="#10B981" strokeWidth={2} strokeDasharray="5 5" name="Mínimo" dot={false} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {monitoringHistory && monitoringHistory.length > 0 && (
                <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '4px' }}>
                  <div style={{ padding: '10px 12px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Zap size={14} color="#2563eb" />
                    <h3 style={{ margin: 0, fontSize: '12px', fontWeight: '600', color: '#334155' }}>Volumen de Peticiones Diario</h3>
                  </div>
                  <div style={{ padding: '12px' }}>
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={monitoringHistory}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis dataKey="date" stroke="#94A3B8" fontSize={9} tickFormatter={(value) => new Date(value).toLocaleDateString('es-ES', { day: 'numeric' })} />
                        <YAxis stroke="#94A3B8" fontSize={10} />
                        <Tooltip contentStyle={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '11px' }} />
                        <Legend wrapperStyle={{ fontSize: '10px' }} />
                        <Bar dataKey="successful_requests" stackId="a" fill="#10B981" name="Exitosas" />
                        <Bar dataKey="failed_requests"     stackId="a" fill="#EF4444" radius={[4,4,0,0]} name="Fallidas" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {monitoringHistory && monitoringHistory.length > 0 && (
                <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '4px' }}>
                  <div style={{ padding: '10px 12px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <CheckCircle2 size={14} color="#10b981" />
                    <h3 style={{ margin: 0, fontSize: '12px', fontWeight: '600', color: '#334155' }}>Tasa de Éxito Diaria (%)</h3>
                  </div>
                  <div style={{ padding: '12px' }}>
                    <ResponsiveContainer width="100%" height={220}>
                      <AreaChart data={monitoringHistory.map(m => ({ ...m, success_rate: m.total_requests > 0 ? ((m.successful_requests / m.total_requests) * 100).toFixed(2) : 0 }))}>
                        <defs><linearGradient id="colorSuccess" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#10B981" stopOpacity={0.3} /><stop offset="95%" stopColor="#10B981" stopOpacity={0} /></linearGradient></defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis dataKey="date" stroke="#94A3B8" fontSize={9} tickFormatter={(value) => new Date(value).toLocaleDateString('es-ES', { day: 'numeric' })} />
                        <YAxis stroke="#94A3B8" fontSize={10} domain={[80, 100]} ticks={[80,85,90,95,100]} />
                        <Tooltip contentStyle={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '11px' }} formatter={(value) => `${value}%`} />
                        <Area type="monotone" dataKey="success_rate" stroke="#10B981" fill="url(#colorSuccess)" strokeWidth={2.5} name="Tasa de Éxito" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {endpointStats && endpointStats.length > 0 && (
                <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '4px' }}>
                  <div style={{ padding: '10px 12px', borderBottom: '1px solid #f1f5f9' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Activity size={14} color="#2563eb" />
                      <h3 style={{ margin: 0, fontSize: '12px', fontWeight: '600', color: '#334155' }}>Top Endpoints</h3>
                      <HelpTooltip text="Rutas más utilizadas del API en el período seleccionado" />
                    </div>
                  </div>
                  <div style={{ padding: '8px', maxHeight: '280px', overflowY: 'auto' }}>
                    {endpointStats.slice(0, 10).map((ep, idx) => (
                      <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 8px', marginBottom: idx < 9 ? '4px' : 0, background: '#f8fafc', borderRadius: '3px' }}>
                        <div style={{ width: '20px', height: '20px', background: '#3B82F6', color: 'white', borderRadius: '3px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: '700', flexShrink: 0 }}>{idx + 1}</div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '10px', fontWeight: '600', color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ep.endpoint}</div>
                          <div style={{ fontSize: '9px', color: '#94a3b8' }}>{ep.avgTime}ms avg · {ep.successRate}% success</div>
                        </div>
                        <div style={{ fontSize: '12px', fontWeight: '700', color: '#3B82F6', flexShrink: 0 }}>{ep.count.toLocaleString()}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {errorStats && errorStats.by_type && errorStats.by_type.length > 0 && (
                <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '4px' }}>
                  <div style={{ padding: '10px 12px', borderBottom: '1px solid #f1f5f9' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <XOctagon size={14} color="#ef4444" />
                      <h3 style={{ margin: 0, fontSize: '12px', fontWeight: '600', color: '#334155' }}>Tipos de Errores</h3>
                      <HelpTooltip text="Errores agrupados por tipo con ejemplos" />
                      <span style={{ marginLeft: 'auto', padding: '2px 6px', background: '#fee2e2', color: '#991b1b', borderRadius: '3px', fontSize: '9px', fontWeight: '600' }}>{errorStats.total} total</span>
                    </div>
                  </div>
                  <div style={{ padding: '8px', maxHeight: '280px', overflowY: 'auto' }}>
                    {errorStats.by_type.slice(0, 8).map((error, idx) => (
                      <div key={idx} style={{ padding: '8px', marginBottom: idx < 7 ? '6px' : 0, background: '#fef2f2', borderRadius: '4px', border: '1px solid #fecaca' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                          <span style={{ fontSize: '11px', fontWeight: '600', color: '#991b1b' }}>{error.type}</span>
                          <span style={{ padding: '2px 8px', background: '#dc2626', color: 'white', borderRadius: '10px', fontSize: '10px', fontWeight: '700' }}>{error.count}</span>
                        </div>
                        {error.examples && error.examples.length > 0 && (
                          <div style={{ fontSize: '9px', color: '#64748b' }}>{error.examples[0].message.substring(0, 60)}...</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {downtimeData && downtimeData.periods && downtimeData.periods.length > 0 && (
                <div style={{ gridColumn: '1 / -1', background: 'white', border: '1px solid #e2e8f0', borderRadius: '4px' }}>
                  <div style={{ padding: '10px 12px', borderBottom: '1px solid #f1f5f9' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <WifiOff size={14} color="#ef4444" />
                      <h3 style={{ margin: 0, fontSize: '12px', fontWeight: '600', color: '#334155' }}>Períodos de Downtime</h3>
                    </div>
                  </div>
                  <div style={{ overflowX: 'auto', padding: '0 12px 12px' }}>
                    <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                          {['Inicio', 'Fin', 'Duración', 'Estado'].map((header, idx) => (
                            <th key={idx} style={{ padding: '8px', textAlign: 'left', fontWeight: '600', color: '#64748b', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{header}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {downtimeData.periods.map((period, idx) => (
                          <tr key={idx} style={{ borderBottom: '1px solid #f8fafc', background: '#fef2f2' }}>
                            <td style={{ padding: '8px', color: '#64748b' }}>{new Date(period.start).toLocaleString('es-ES')}</td>
                            <td style={{ padding: '8px', color: '#64748b' }}>{new Date(period.end).toLocaleString('es-ES')}</td>
                            <td style={{ padding: '8px', fontWeight: '600' }}><span style={{ padding: '2px 8px', background: '#fee2e2', color: '#dc2626', borderRadius: '10px', fontSize: '10px', fontWeight: '700' }}>{period.duration_minutes} min</span></td>
                            <td style={{ padding: '8px' }}><span style={{ padding: '2px 8px', background: '#dc2626', color: 'white', borderRadius: '10px', fontSize: '10px', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '4px' }}><WifiOff size={10} />OFFLINE</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* ══════════════ TAB: ERP ══════════════ */}
        {activeTab === 'erp' && (
          <>
            {erpSubTab === 'latencia' && <LatencyPanel />}
            {erpSubTab === 'stock'    && <StockERPPanel stats={stats} productAnalysis={productAnalysis} stockData={erpStockData} stockLoading={erpStockLoading} stockError={erpStockError} onRefresh={fetchErpStockData} />}
          </>
        )}
      </div>

      {/* Modal Leyenda KPI */}
      {kpisData && (
        <LegendModal isOpen={showLegendModal} onClose={() => setShowLegendModal(false)} leyenda={kpisData.leyenda || []} />
      )}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  )
}

export default Analytics